import { getLeadConfirmationMessage } from '../../../backend/prompts/conversation-policy.js';
import { emitOtpFunnelClientEvent, type OtpFunnelClientReason } from './otpFunnelTelemetry.js';
import { normalizeBookingPhone } from './bookingOtp.js';
import { OTOKOC_LAUNCHER_AVATAR_URL } from './config.js';
import { resolveOtokocDeviceContext } from './deviceMetadata.js';
import {
  ELEVENLABS_BROWSER_SDK_VERSION,
  resolveElevenLabsBrowserSdkUrl,
  startElevenLabsVoiceSession,
  type DisconnectionDetails,
} from './elevenLabsBrowserClient.js';
import { closeUnifiedBookingOtpFlow, openUnifiedBookingOtpFlow } from './unifiedBookingOtpFlow.js';
import {
  VoiceClientToolDiagnostics,
  VoiceClientToolError,
  fetchVoiceClientToolJson,
  type VoiceClientToolDiagnostic,
} from './voiceClientDiagnostics.js';

/**
 * Otokoç AI Voice Launcher — testchat harness
 *
 * Vanilla-JS floating voice widget. Call mountVoiceLauncher() with the
 * backend API base URL and it appends itself to document.body. Returns a
 * cleanup function that removes the widget and ends any active session.
 *
 * Adapted from robot-engine-lean/voiceAssistantLauncher.ts.
 * CSS is injected inline into <head> on first call.
 */

type VoicePanelState =
  | 'idle'
  | 'connecting'
  | 'speaking'
  | 'listening'
  | 'transferring'
  | 'waiting_sms'
  | 'saving'
  | 'error';
type OtpHandoffSignal = 'tool' | 'phrase';
type VoiceOtpOpenReason = OtpHandoffSignal | 'ready_for_otp';
type VoiceOtpCloseReason = 'dismiss' | 'verified' | null;
type VoiceOtpRetry = {
  sessionKey: string;
  phone: string | null;
};
type SessionCallbacks = {
  onConnect?: (props?: { conversationId?: string }) => void;
  onDisconnect?: (details?: DisconnectionDetails) => void;
  onError?: (message: string, context?: unknown) => void;
  onModeChange?: (mode: { mode: 'speaking' | 'listening' }) => void;
  onMessage?: (message: { message?: string; role?: string; source?: string }) => void;
};
type VoiceSession = {
  endSession(): Promise<void>;
  conversation_id?: string;
  getId?: () => string;
  setMicMuted?: (muted: boolean) => void;
  setVolume?: (options: { volume: number }) => void;
  sendContextualUpdate?: (message: string) => void;
  sendUserMessage?: (message: string) => void;
  sendUserActivity?: () => void;
};
interface VoiceLauncherOptions {
  middlewareUrl: string;
  onOpenChat?: (initialMessage?: string) => void;
}
type SaveConversationResponse = {
  ok?: boolean;
  lead?: {
    phone?: unknown;
  };
};
type VoiceEndReason = 'manual_end' | 'disconnect' | 'end_button' | 'automatic_end';

type VoiceConversationSaveMetadata = ReturnType<typeof resolveOtokocDeviceContext> & {
  clientSdkVersion: typeof ELEVENLABS_BROWSER_SDK_VERSION;
  clientToolDiagnostics: VoiceClientToolDiagnostic[];
  voiceEndReason?: VoiceEndReason;
  userSpeechCount: number;
};

type VoiceBookingIntent = 'technical_service' | 'test_drive' | 'general_info' | 'service';

type VoiceBookingFields = {
  full_name?: string;
  phone?: string;
  email?: string;
  brand_model?: string;
  brand_model_declined?: boolean;
  city?: string;
  location_id?: string;
  preferred_datetime?: string;
  pending_datetime_raw_text?: string;
  pending_datetime_status?: 'absent' | 'pending' | 'resolved';
  pending_datetime_reason?:
    'missing_location_context' | 'missing_time' | 'missing_date' | 'unclear';
  request_subject?: string;
};

type VoiceBookingLocationOption = {
  id?: string;
  branch_name?: string;
  city?: string;
};

type VoiceBookingValidationResult = {
  intent?: VoiceBookingIntent;
  fields?: VoiceBookingFields;
  required_fields?: string[];
  missing_fields?: string[];
  next_field?: string | null;
  ready_for_otp?: boolean;
  trace_revision?: number;
  validated_at?: number;
  assistant_guidance?: string;
  next_question_suggestion?: string;
  response_policy?: string;
  location_context?: {
    city_selected?: boolean;
    branch_options_ready?: boolean;
    eligible_cities?: string[];
    eligible_locations?: VoiceBookingLocationOption[];
  };
  field_messages?: Partial<
    Record<
      'full_name' | 'phone' | 'email' | 'brand_model' | 'location_id' | 'preferred_datetime',
      string
    >
  >;
};

type VoiceBookingToolState = {
  traceRevision?: number;
  validatedAt?: number;
  intent?: VoiceBookingIntent;
  fields: VoiceBookingFields;
  requiredFields: string[];
  nextField: string | null;
  missingFields: string[];
  readyForOtp: boolean;
  referralGuidance?: string;
};

type VoiceInfoCards = {
  primaryTitle: string;
  primaryDetail: string;
  secondaryTitle: string;
  secondaryDetail: string;
};

const VOICE_BOOKING_FIELD_LABELS: Record<string, string> = {
  full_name: 'Ad soyad',
  phone: 'Telefon',
  email: 'E-posta',
  brand_model: 'Marka / model',
  city: 'Şehir',
  location_id: 'Şube',
  preferred_datetime: 'Tarih / saat',
  request_subject: 'Talep konusu',
};

const VOICE_BOOKING_FIELD_PROGRESS_TITLES: Record<string, string> = {
  full_name: 'İsim bilgisi doğrulanıyor',
  phone: 'Telefon bilgisi doğrulanıyor',
  email: 'E-posta bilgisi doğrulanıyor',
  brand_model: 'Araç bilgisi doğrulanıyor',
  city: 'Şehir bilgisi doğrulanıyor',
  location_id: 'Şube bilgisi doğrulanıyor',
  preferred_datetime: 'Randevu zamanı doğrulanıyor',
  request_subject: 'Talep konusu doğrulanıyor',
};

const VOICE_SUPPORTED_BRAND_ALIASES: Array<{ label: string; aliases: string[] }> = [
  { label: 'Ford', aliases: ['ford'] },
  { label: 'Volvo', aliases: ['volvo'] },
  { label: 'Fiat', aliases: ['fiat'] },
  { label: 'Jeep', aliases: ['jeep'] },
  { label: 'Alfa Romeo', aliases: ['alfa romeo'] },
  { label: 'Maserati', aliases: ['maserati'] },
  { label: 'Opel', aliases: ['opel'] },
  { label: 'Peugeot', aliases: ['peugeot'] },
  { label: 'Citroën', aliases: ['citroen', 'citroën'] },
];

type VoiceTracePayload = {
  event: string;
  clientSdkVersion?: typeof ELEVENLABS_BROWSER_SDK_VERSION;
  conversationId?: string | null;
  otpHandoffPending?: boolean;
  handoffSignal?: OtpHandoffSignal | null;
  mode?: 'speaking' | 'listening';
  role?: string | null;
  source?: string | null;
  snippet?: string;
  phone?: string | null;
  note?: string;
  bookingIntent?: VoiceBookingIntent;
  requiredFields?: string[];
  missingFields?: string[];
  nextField?: string | null;
  readyForOtp?: boolean;
  validatedFields?: VoiceBookingFields;
  fieldMessages?: Partial<
    Record<
      'full_name' | 'phone' | 'email' | 'brand_model' | 'location_id' | 'preferred_datetime',
      string
    >
  >;
  traceRevision?: number;
  spokenField?: string | null;
};

export function shouldTriggerOtpTransfer(message: string): boolean {
  const normalized = message.toLocaleLowerCase('tr-TR');
  const mentionsOtpChannel =
    normalized.includes('sms') ||
    normalized.includes('kısa mesaj') ||
    normalized.includes('kisa mesaj');
  const mentionsHandoff =
    normalized.includes('aktarıyorum') ||
    normalized.includes('aktaracağım') ||
    normalized.includes('yönlendiriyorum') ||
    normalized.includes('yonlendiriyorum') ||
    normalized.includes('yönlendireceğim') ||
    normalized.includes('yonlendirecegim');
  const mentionsOtpStep =
    normalized.includes('onay adımına') ||
    normalized.includes('onay adimina') ||
    normalized.includes('onayına') ||
    normalized.includes('onayina') ||
    normalized.includes('otp') ||
    normalized.includes('kod') ||
    normalized.includes('doğrulama') ||
    normalized.includes('dogrulama');

  return mentionsOtpChannel && (mentionsHandoff || mentionsOtpStep);
}

export function resolveVoiceOtpOpenReason(input: {
  otpHandoffPending: boolean;
  handoffSignal: OtpHandoffSignal | null;
  readyForOtp: boolean;
}): VoiceOtpOpenReason | null {
  if (!input.readyForOtp) {
    return null;
  }

  if (input.otpHandoffPending) {
    return input.handoffSignal ?? 'phrase';
  }

  if (input.readyForOtp) {
    return 'ready_for_otp';
  }

  return null;
}

export function shouldSkipVoiceOtpOverlayOpen(input: {
  lastConversationId: string | null;
  nextConversationId: string;
  overlayOpen: boolean;
  lastCloseReason: VoiceOtpCloseReason;
}): boolean {
  if (input.lastConversationId !== input.nextConversationId) {
    return false;
  }

  return input.overlayOpen || input.lastCloseReason === 'verified';
}

function createEmptyVoiceBookingToolState(): VoiceBookingToolState {
  return {
    intent: undefined,
    fields: {},
    requiredFields: [],
    nextField: null,
    missingFields: [],
    readyForOtp: false,
  };
}

export function extractTurkishPhoneFromTranscript(raw: string): string | null {
  const compact = raw.replace(/[\s\u00a0\-().]/g, '');
  const match = compact.match(/(?:^|[^\d])(0?5\d{9})(?:[^\d]|$)/);
  if (!match?.[1]) {
    return null;
  }
  return normalizeBookingPhone(match[1]);
}

/**
 * OTP phone precedence (issue #118): validated voice booking session phone
 * first, then the saved conversation lead phone, and role-agnostic transcript
 * reconstruction only as a last resort.
 */
export function resolveOtpPhone(
  saveResponse: SaveConversationResponse | null,
  transcript: string,
  validatedPhone?: string
): string | null {
  const toolPhone =
    typeof validatedPhone === 'string' ? normalizeBookingPhone(validatedPhone) : null;
  const leadPhone =
    saveResponse?.lead && typeof saveResponse.lead.phone === 'string'
      ? saveResponse.lead.phone
      : '';
  return (
    toolPhone || normalizeBookingPhone(leadPhone) || extractTurkishPhoneFromTranscript(transcript)
  );
}

/**
 * Validation fallback text prefers the tool-provided utterance, then the most
 * recent user utterance. The full transcript is intentionally never sent: it
 * mixes assistant wording (dates, digits) into user-evidence checks.
 */
export function resolveVoiceValidationFallbackText(
  explicitFallbackText: string | undefined,
  recentUserUtterances: string[]
): string | undefined {
  return explicitFallbackText || recentUserUtterances[recentUserUtterances.length - 1] || undefined;
}

function normalizeVoiceBrandText(value: string): string {
  return value
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeVoiceBrandPattern(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
}

export function extractVoiceBrandModelMention(raw: string): string | undefined {
  const normalized = normalizeVoiceBrandText(raw);
  if (!normalized) {
    return undefined;
  }

  let latestMention: { index: number; label: string } | null = null;
  for (const brand of VOICE_SUPPORTED_BRAND_ALIASES) {
    for (const alias of brand.aliases) {
      const pattern = new RegExp(
        `(?:^|[^\\p{L}\\p{N}])${escapeVoiceBrandPattern(normalizeVoiceBrandText(alias))}(?=$|[^\\p{L}\\p{N}])`,
        'u'
      );
      const match = pattern.exec(normalized);
      if (match && (!latestMention || match.index >= latestMention.index)) {
        latestMention = { index: match.index, label: brand.label };
      }
    }
  }

  return latestMention?.label;
}

export function inferVoiceBookingQuestionField(message: string): string | null {
  const normalized = message.toLocaleLowerCase('tr-TR');
  if (shouldTriggerOtpTransfer(message)) {
    return 'otp_handoff';
  }
  if (/\b(?:h[âa]l[âa] orada mısınız|orada mısınız)\b/u.test(normalized)) {
    return 'presence_check';
  }
  if (
    /\b(?:ad(?:ınız|iniz|ın|in)?(?: soyad(?:ınız|iniz)?)?|isim(?: soyisim)?)\b/u.test(normalized)
  ) {
    return 'full_name';
  }
  if (/\b(?:telefon(?: numaran(?:ız|iz))?|cep telefonu|gsm)\b/u.test(normalized)) {
    return 'phone';
  }
  if (/\b(?:e-?posta|mail adres)\b/u.test(normalized)) {
    return 'email';
  }
  if (/\b(?:marka(?:sını|si)?|model(?:ini|i)?|araç(?:ın|inizin)? marka)\b/u.test(normalized)) {
    return 'brand_model';
  }
  if (
    /\b(?:hangi şehir|hangi şube|şube sizin için|şehir sizin için|lokasyon)\b/u.test(normalized)
  ) {
    return 'location_id';
  }
  if (
    /\b(?:hangi gün|hangi tarih|tarih|saat kaçta|hangi saat|randevu almak istiyorsunuz)\b/u.test(
      normalized
    )
  ) {
    return 'preferred_datetime';
  }
  if (/\b(?:hangi konuda|taleb(?:inizi|in)|ne hakkında)\b/u.test(normalized)) {
    return 'request_subject';
  }
  return null;
}

const DEFAULT_SESSION_NOTE = 'Konuşma kayıt altına alınır.';
const VOICE_SMS_HANDOFF_MESSAGE =
  'Telefon numaranızı ekranda kontrol edip SMS Gönder düğmesine basın, ardından gelen kodu girin.';
const SMS_VERIFICATION_WAIT_TIMEOUT_MS = 115_000;
const VOICE_BOOKING_VALIDATION_TIMEOUT_MS = 30_000;
const VOICE_OTP_OPEN_AFTER_SPEECH_MS = 1_000;
const VOICE_OTP_SPEECH_RECHECK_MS = 250;
const VOICE_VERIFIED_CLOSE_AFTER_LISTENING_MS = 3_000;
const VOICE_VERIFIED_CLOSE_FALLBACK_MS = 10_000;
const VOICE_LEAD_CONTEXT_MAX_MESSAGES = 24;
const VOICE_CONTEXT_MESSAGE_MAX_LENGTH = 400;
const DESKTOP_INTENT_ACTIONS = [
  {
    label: 'Servis randevusu',
    message: 'Servis randevusu almak istiyorum',
  },
  {
    label: 'Test sürüşü',
    message: 'Test sürüşü randevusu almak istiyorum',
  },
] as const;

const STATE_LABELS: Record<
  VoicePanelState,
  { headline: string; detail: string; durationVisible: boolean }
> = {
  idle: {
    headline: 'Hazır',
    detail: 'Başlatmak için butona dokunun.',
    durationVisible: false,
  },
  connecting: {
    headline: 'Bağlanıyor...',
    detail: '',
    durationVisible: false,
  },
  speaking: {
    headline: 'Yanıtlıyor',
    detail: '',
    durationVisible: true,
  },
  listening: {
    headline: 'Dinliyor',
    detail: '',
    durationVisible: true,
  },
  transferring: {
    headline: 'SMS adımı',
    detail: '',
    durationVisible: false,
  },
  waiting_sms: {
    headline: 'Telefon doğrulama',
    detail: 'Numaranızı kontrol edip SMS gönderin, ardından gelen kodu girin.',
    durationVisible: false,
  },
  saving: {
    headline: 'Kaydediliyor',
    detail: '',
    durationVisible: false,
  },
  error: {
    headline: 'Bağlantı kurulamadı',
    detail: 'Tekrar deneyin.',
    durationVisible: false,
  },
};

const STATE_INFO_CARDS: Record<VoicePanelState, VoiceInfoCards> = {
  idle: {
    primaryTitle: 'Görüşmeye hazır',
    primaryDetail: 'Başlatmak için arama butonuna dokunun.',
    secondaryTitle: 'Sesli asistan bekliyor',
    secondaryDetail: 'Bağlantı kurulunca sizi dinleyecek.',
  },
  connecting: {
    primaryTitle: 'Bağlantı kuruluyor',
    primaryDetail: 'Mikrofon ve ses oturumu hazırlanıyor.',
    secondaryTitle: 'Asistan aranıyor',
    secondaryDetail: 'Cevap verince konuşma başlayacak.',
  },
  speaking: {
    primaryTitle: 'Asistan yanıtlıyor',
    primaryDetail: 'Yanıt bitince mikrofon tekrar sizi dinler.',
    secondaryTitle: 'Bilgiler takip ediliyor',
    secondaryDetail: 'Gerekli alanlar konuşma içinde güncellenir.',
  },
  listening: {
    primaryTitle: 'Sizi dinliyor',
    primaryDetail: 'Mikrofon açık, konuşabilirsiniz.',
    secondaryTitle: 'Yanıt hazırlanıyor',
    secondaryDetail: 'Söyledikleriniz akışa göre değerlendirilir.',
  },
  transferring: {
    primaryTitle: 'SMS doğrulamaya geçiliyor',
    primaryDetail: 'Kod ekranı hazırlanıyor.',
    secondaryTitle: 'Görüşme kısa süre beklemede',
    secondaryDetail: 'Doğrulama bitince süreç kaldığı yerden devam eder.',
  },
  waiting_sms: {
    primaryTitle: 'Telefon doğrulama',
    primaryDetail: 'Numaranızı kontrol edip SMS gönderin.',
    secondaryTitle: 'Kod bekleniyor',
    secondaryDetail: 'SMS kodu girilince görüşme kaldığı yerden devam eder.',
  },
  saving: {
    primaryTitle: 'Görüşme kaydediliyor',
    primaryDetail: 'Konuşma güvenli şekilde tamamlanıyor.',
    secondaryTitle: 'Son adım hazırlanıyor',
    secondaryDetail: 'Uygun ise SMS doğrulama ekranı açılır.',
  },
  error: {
    primaryTitle: 'Bağlantı kurulamadı',
    primaryDetail: 'Tekrar başlatmayı deneyin.',
    secondaryTitle: 'Ses oturumu kapandı',
    secondaryDetail: 'Bağlantı bilgileri yenilenerek tekrar denenir.',
  },
};

function getVoiceBookingIntentLabel(intent?: VoiceBookingIntent): string {
  if (intent === 'technical_service' || intent === 'service') {
    return 'Servis talebi';
  }
  if (intent === 'test_drive') {
    return 'Test sürüşü talebi';
  }
  if (intent === 'general_info') {
    return 'Bilgi talebi';
  }
  return 'Talebiniz';
}

const CSS_ID = 'glov-voice-css';

function ensureStyles() {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = `
    .glov-voice-chat-container {
      width: auto; min-width: auto;
      max-width: min(100vw - 48px - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px), 380px);
      height: 58px;
      position: fixed; bottom: 24px; right: 24px; z-index: 999999;
      display: flex; flex-direction: column; align-items: stretch;
      border-radius: 14px; overflow: visible; background: transparent;
    }
    .glov-voice-chat-container.glov-voice-chat-desktop-hidden { display: none; }
    .glov-voice-chat-container.glov-voice-chat-mobile {
      min-width: auto;
      max-width: min(100vw - 24px - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px), 370px);
      right: max(12px, env(safe-area-inset-right, 0px));
      /* Clear fixed host nav (e.g. bottom action bar) + safe area */
      bottom: calc(max(12px, env(safe-area-inset-bottom, 0px)) + 50px);
      isolation: isolate;
    }
    .glov-voice-chat-container.glov-voice-chat-expanded { height: 720px; max-width: 400px; }
    .glov-voice-chat-container.glov-voice-chat-panel-open { pointer-events: none; }
    /* Mobile: panel is portaled to body; collapse launcher slot so pointer-events:none does not leave a click-through hole */
    .glov-voice-chat-container.glov-voice-chat-mobile.glov-voice-chat-panel-open {
      visibility: hidden;
      width: 0;
      min-width: 0;
      max-width: 0;
      height: 0;
      overflow: hidden;
      padding: 0;
      border: none;
    }
    .glov-voice-chat-container.glov-voice-chat-panel-open .glov-voice-chat-container-inner {
      opacity: 0; transform: scale(0.98); pointer-events: none; visibility: hidden;
    }
    .glov-voice-chat-container.glov-voice-chat-panel-open .glov-voice-chat-intents {
      opacity: 0; transform: translateY(6px) scale(0.98); pointer-events: none; visibility: hidden;
    }
    .glov-voice-chat-container.glov-voice-chat-panel-open .glov-voice-chat-otp-retry {
      display: none;
    }
    .glov-voice-chat-intents {
      position: absolute;
      right: 0;
      bottom: 70px;
      width: min(356px, calc(100vw - 48px));
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 8px;
      pointer-events: auto;
      z-index: 1;
      transition: opacity 0.16s ease, transform 0.16s ease, visibility 0s linear 0.16s;
    }
    .glov-voice-chat-intent-btn {
      min-height: 36px;
      max-width: 100%;
      padding: 8px 14px;
      border: 1px solid rgba(226, 232, 240, 0.96);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.96);
      color: #0f2b5b;
      font: 700 13px/1.15 "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      letter-spacing: 0;
      white-space: nowrap;
      cursor: pointer;
      box-shadow: 0 14px 34px rgba(15, 23, 42, 0.14), 0 3px 10px rgba(15, 23, 42, 0.08);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      transition:
        background 0.16s ease,
        border-color 0.16s ease,
        color 0.16s ease,
        transform 0.16s ease,
        box-shadow 0.16s ease;
    }
    .glov-voice-chat-intent-btn:hover,
    .glov-voice-chat-intent-btn:focus-visible {
      background: #ff8000;
      border-color: #ff8000;
      color: #ffffff;
      transform: translateY(-1px);
      box-shadow: 0 16px 36px rgba(249, 115, 22, 0.24), 0 4px 12px rgba(15, 23, 42, 0.08);
      outline: none;
    }
    .glov-voice-chat-otp-retry {
      position: absolute;
      right: 0;
      bottom: 70px;
      z-index: 3;
      min-height: 40px;
      max-width: min(320px, calc(100vw - 24px));
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 10px 16px;
      border: 1px solid #ff8000;
      border-radius: 999px;
      background: #ffffff;
      color: #c45d00;
      font: 700 13px/1.2 "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      cursor: pointer;
      box-shadow: 0 14px 34px rgba(15, 23, 42, 0.16), 0 3px 10px rgba(15, 23, 42, 0.08);
    }
    .glov-voice-chat-otp-retry:hover,
    .glov-voice-chat-otp-retry:focus-visible {
      background: #ff8000;
      color: #ffffff;
      outline: none;
    }
    .glov-voice-chat-otp-retry[hidden] {
      display: none;
    }
    .glov-voice-chat-container-inner {
      width: 100%; height: 58px;
      background: rgba(255,255,255,0.95);
      border: 1px solid #f1f5f9; border-radius: 999px;
      display: flex; align-items: center; justify-content: flex-start;
      padding: 8px; gap: 12px; flex-shrink: 0;
      box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.22), 0 8px 16px rgba(15, 23, 42, 0.08);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      transition: opacity 0.16s ease, transform 0.16s ease, visibility 0s linear 0.16s;
      position: relative; z-index: 2;
    }
    .glov-voice-chat-actions {
      display: flex; align-items: center; gap: 8px;
      flex: 1 1 auto; min-width: 0;
    }
    .glov-voice-chat-call-button {
      display: inline-flex; align-items: center; justify-content: center;
      gap: 8px; min-height: 40px; padding: 8px 16px; border: none; border-radius: 999px;
      background: #ff8000;
      color: #ffffff; font-size: 14px; font-weight: 700; cursor: pointer;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08); flex: 1 1 auto; min-width: 0;
      transition: background 0.18s ease, box-shadow 0.18s ease;
      letter-spacing: -0.025em;
    }
    .glov-voice-chat-call-button:hover {
      transform: none;
      filter: none;
      background: #e67300;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
    }
    .glov-voice-chat-open-chat-btn {
      display: none; align-items: center; justify-content: center;
      width: 40px; min-width: 40px; height: 40px; padding: 0;
      border: 1px solid #e2e8f0; border-radius: 999px; background: #f8fafc;
      color: #0f172a; cursor: pointer;
      transition: background 0.18s ease, border-color 0.18s ease;
      overflow: hidden; flex: 0 0 auto;
    }
    .glov-voice-chat-open-chat-btn:hover { background: #f1f5f9; transform: none; }
    .glov-voice-chat-open-chat-icon { width: 20px; height: 20px; fill: currentColor; }
    .glov-voice-chat-open-chat-btn img { display: none !important; }
    .glov-voice-chat-call-icon { width: 20px; height: 20px; flex: 0 0 auto; color: #ffffff; }
    .glov-voice-chat-call-label {
      color: #ffffff !important;
      text-decoration: none !important;
      font-weight: 700;
      letter-spacing: -0.025em;
      line-height: 1;
      white-space: nowrap;
    }
    .glov-voice-chat-btn-avatar-wrap { position: relative; display: inline-flex; align-items: center; flex-shrink: 0; }
    .glov-voice-chat-btn-avatar {
      width: 40px; height: 40px; border-radius: 50%; object-fit: cover;
      border: 1px solid #f1f5f9; opacity: 1; display: block;
    }
    .glov-voice-chat-btn-live-dot {
      display: block; position: absolute; bottom: 0; right: 0;
      width: 12px; height: 12px; border-radius: 50%;
      background: #22c55e; border: 2px solid #ffffff;
      animation: glov-live-pulse 2s ease-in-out infinite;
    }
    .glov-voice-chat-btn-phone-icon { display: none; }
    .glov-voice-chat-panel {
      position: absolute; inset: 0;
      background:
        radial-gradient(circle at 50% 16%, rgba(165,81,13,0.62), transparent 34%),
        radial-gradient(circle at 50% 28%, rgba(237,145,36,0.18), transparent 45%),
        linear-gradient(180deg, #2d1308 0%, #130904 34%, #050404 70%, #020202 100%);
      border: 1px solid rgba(255,255,255,0.08); border-radius: 22px;
      box-shadow: 0 24px 52px rgba(0,0,0,0.38);
      transition: transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.22s ease;
      transform: translateY(10px) scale(0.985);
      opacity: 0; pointer-events: none; overflow: hidden;
      z-index: 9999;
    }
    .glov-voice-chat-panel::before {
      content: '';
      position: absolute;
      inset: -18% -44% auto;
      height: 58%;
      background:
        radial-gradient(circle at center, rgba(151,72,14,0.34), transparent 54%),
        radial-gradient(circle at center, rgba(254,190,98,0.12), transparent 68%);
      pointer-events: none;
      opacity: 0.9;
    }
    .glov-voice-chat-panel.glov-voice-chat-fullscreen {
      position: fixed;
      inset: var(--glov-voice-viewport-top, 0px) 0 auto;
      width: 100vw;
      max-width: 100vw;
      height: 100vh;
      height: var(--glov-voice-viewport-height, 100dvh);
      max-height: 100vh;
      max-height: var(--glov-voice-viewport-height, 100dvh);
      border-radius: 0;
      box-sizing: border-box;
      z-index: 2147483001;
      touch-action: manipulation;
    }
    .glov-voice-chat-panel.glov-voice-chat-fullscreen .glov-voice-chat-panel-hit-layer {
      display: block;
      position: absolute;
      inset: 0;
      z-index: 0;
      pointer-events: auto;
      background: transparent;
    }
    .glov-voice-chat-panel.glov-voice-chat-fullscreen .glov-voice-chat-panel-content {
      position: relative;
      z-index: 1;
    }
    .glov-voice-chat-panel.glov-voice-chat-fullscreen .glov-voice-chat-main {
      min-height: 0;
    }
    .glov-voice-chat-panel.glov-voice-chat-fullscreen .glov-voice-chat-panel-header,
    .glov-voice-chat-panel.glov-voice-chat-fullscreen .glov-voice-chat-panel-footer {
      flex-shrink: 0;
    }
    .glov-voice-chat-panel.glov-voice-chat-fullscreen .glov-voice-chat-panel-footer {
      position: relative;
      z-index: 3;
    }
    .glov-voice-chat-panel.active { transform: translateY(0) scale(1); opacity: 1; pointer-events: auto; z-index: 9999; }
    .glov-voice-chat-panel.glov-voice-chat-fullscreen.active {
      z-index: 2147483001;
    }
    .glov-voice-chat-panel-hit-layer { display: none; }
    .glov-voice-chat-panel-content {
      position: relative;
      z-index: 1;
      width: 100%; height: 100%;
      display: flex; flex-direction: column; justify-content: space-between;
      gap: 14px;
      padding:
        calc(22px + env(safe-area-inset-top, 0px))
        22px
        calc(26px + env(safe-area-inset-bottom, 0px));
      box-sizing: border-box;
      color: #fff;
    }
    .glov-voice-chat-panel-header {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }
    .glov-voice-chat-panel-kicker {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.76);
    }
    .glov-voice-chat-main {
      flex: 0 1 auto;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 8px;
      text-align: center;
      padding-top: clamp(8px, 2.8vh, 24px);
    }
    .glov-voice-chat-visual {
      position: relative;
      width: min(88vw, 340px);
      height: min(88vw, 340px);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: -6px;
    }
    .glov-voice-chat-rings {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .glov-voice-chat-ring {
      position: absolute;
      inset: 10%;
      border: 1.5px solid rgba(255,181,85,0.34);
      border-radius: 50%;
      opacity: 0.82;
      transform: scale(0.72);
      pointer-events: none;
      box-shadow:
        0 0 18px rgba(255,145,32,0.18),
        inset 0 0 18px rgba(255,145,32,0.08);
      animation: glov-ring-ripple 5.2s cubic-bezier(0, 0.2, 0.8, 1) infinite paused;
    }
    .glov-voice-chat-ring:nth-child(1) { inset: 29%; opacity: 0.78; }
    .glov-voice-chat-ring:nth-child(2) { inset: 18%; animation-delay: 1s; opacity: 0.62; }
    .glov-voice-chat-ring:nth-child(3) { inset: 7%; animation-delay: 2s; opacity: 0.46; }
    .glov-voice-chat-ring:nth-child(4) { inset: -4%; animation-delay: 3s; opacity: 0.32; }
    .glov-voice-chat-avatar {
      position: relative;
      z-index: 1;
      width: min(42vw, 168px);
      height: min(42vw, 168px);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #ffffff;
      border: 7px solid #f3d7a6;
      box-shadow:
        0 0 0 1px rgba(255,255,255,0.28),
        0 0 42px rgba(235,156,55,0.42),
        0 16px 40px rgba(0,0,0,0.32);
      animation: glov-avatar-breathe 2.8s ease-in-out infinite;
      overflow: hidden;
    }
    .glov-voice-chat-avatar-img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      object-position: center 38%;
    }
    .glov-voice-chat-avatar-label {
      display: none;
      font-size: clamp(42px, 11vw, 64px);
      font-weight: 700;
      letter-spacing: -0.04em;
      color: #fff;
      text-shadow: 0 0 16px rgba(255,255,255,0.24);
    }
    .glov-voice-chat-state {
      position: relative;
      z-index: 2;
      display: grid;
      justify-items: center;
      gap: 6px;
      width: min(100%, 320px);
      margin-top: -46px;
    }
    .glov-voice-chat-status-meta {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0;
      min-height: 38px;
      padding: 0 17px;
      border: 1px solid rgba(255,255,255,0.14);
      border-radius: 999px;
      background: rgba(16,14,13,0.74);
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.08),
        0 10px 28px rgba(0,0,0,0.28);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }
    .glov-voice-chat-status-meta::before {
      content: '';
      width: 10px;
      height: 10px;
      margin-right: 9px;
      border-radius: 999px;
      background: #ffc15f;
      box-shadow: 0 0 14px rgba(255,193,95,0.58);
    }
    .glov-voice-chat-state-headline {
      margin: 0;
      font-size: 16px;
      font-weight: 800;
      line-height: 1;
      letter-spacing: 0;
      color: rgba(255,255,255,0.96);
    }
    .glov-voice-chat-status-copy {
      margin: 0;
      max-width: 220px;
      font-size: 13px;
      line-height: 1.45;
      color: rgba(255,255,255,0.5);
    }
    .glov-voice-chat-status-copy[data-empty='true'] {
      display: none;
    }
    .glov-voice-chat-duration {
      font-size: 13px;
      line-height: 1;
      color: rgba(255,255,255,0.42);
      letter-spacing: 0.02em;
    }
    .glov-voice-chat-duration::before {
      content: '';
      display: inline-block;
      width: 1px;
      height: 14px;
      margin: 0 10px;
      vertical-align: -2px;
      background: rgba(255,255,255,0.2);
      color: rgba(255,255,255,0.24);
    }
    .glov-voice-chat-duration[data-visible='false'] {
      display: none;
    }
    .glov-voice-chat-panel-footer {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 18px;
    }
    .glov-voice-chat-info-stack {
      width: 100%;
      display: grid;
      gap: 12px;
    }
    .glov-voice-otp-host {
      width: 100%;
      display: none;
      max-height: min(48dvh, 420px);
      overflow-y: auto;
      overscroll-behavior: contain;
      border-radius: 16px;
    }
    .glov-voice-chat-panel.glov-voice-chat-verifying .glov-voice-otp-host {
      display: block;
    }
    .glov-voice-chat-panel.glov-voice-chat-verifying .glov-voice-chat-info-stack {
      display: none;
    }
    .glov-voice-chat-panel.glov-voice-chat-verifying .glov-voice-chat-main {
      padding-top: 0;
    }
    .glov-voice-chat-panel.glov-voice-chat-verifying .glov-voice-chat-visual {
      width: min(58vw, 220px);
      height: min(58vw, 220px);
      margin-bottom: -26px;
    }
    .glov-voice-chat-panel.glov-voice-chat-verifying .glov-voice-chat-avatar {
      width: min(30vw, 112px);
      height: min(30vw, 112px);
      border-width: 5px;
    }
    .glov-voice-chat-info-card {
      min-height: 68px;
      display: grid;
      grid-template-columns: 44px 1fr;
      align-items: center;
      gap: 14px;
      padding: 12px 14px;
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 10px;
      background: rgba(24,22,22,0.9);
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.06),
        0 10px 24px rgba(0,0,0,0.26);
      box-sizing: border-box;
    }
    .glov-voice-chat-info-icon {
      width: 38px;
      height: 38px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(255,255,255,0.42);
      border-radius: 999px;
      color: rgba(255,255,255,0.86);
    }
    .glov-voice-chat-info-text {
      min-width: 0;
      text-align: left;
    }
    .glov-voice-chat-info-title {
      display: block;
      margin: 0;
      color: rgba(255,255,255,0.96);
      font-size: 16px;
      font-weight: 800;
      line-height: 1.2;
    }
    .glov-voice-chat-info-detail {
      display: block;
      margin: 3px 0 0;
      color: rgba(255,255,255,0.62);
      font-size: 13px;
      font-weight: 700;
      line-height: 1.25;
    }
    .glov-voice-chat-panel-controls {
      width: 100%;
      display: flex;
      align-items: flex-end;
      justify-content: space-around;
      gap: 18px;
    }
    .glov-voice-chat-control {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }
    .glov-voice-chat-control-button {
      position: relative;
      width: 68px;
      height: 68px;
      border-radius: 999px;
      border: 1px solid rgba(255,255,255,0.08);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 14px 28px rgba(0,0,0,0.24);
      transition: transform 0.18s ease, filter 0.18s ease, background 0.18s ease;
      cursor: pointer;
    }
    .glov-voice-chat-control-button:hover:not(:disabled) {
      transform: translateY(-1px);
      filter: brightness(1.03);
    }
    .glov-voice-chat-control-button:disabled {
      cursor: wait;
      opacity: 0.58;
    }
    .glov-voice-chat-control-button-secondary {
      background: rgba(20,21,23,0.96);
      color: rgba(255,255,255,0.84);
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.08),
        0 14px 28px rgba(0,0,0,0.24);
    }
    .glov-voice-chat-control-button-secondary:hover:not(:disabled) {
      background: rgba(28,29,32,0.98);
    }
    .glov-voice-chat-control-button-muted {
      color: rgba(255,255,255,0.38);
      background: rgba(9,10,11,0.92);
    }
    .glov-voice-chat-control-button-muted::after {
      content: '';
      position: absolute;
      width: 34px;
      height: 2px;
      border-radius: 999px;
      background: currentColor;
      transform: rotate(-42deg);
    }
    .glov-voice-chat-control-button-primary {
      background: #d8322a;
      color: #ffffff;
      border-color: rgba(255,255,255,0.08);
    }
    .glov-voice-chat-control-button-primary:hover:not(:disabled) {
      background: #c42b24;
    }
    .glov-voice-chat-control-label {
      font-size: 12px;
      font-weight: 600;
      color: rgba(255,255,255,0.78);
    }
    .glov-voice-chat-session-note {
      width: 100%;
      display: flex;
      justify-content: flex-start;
    }
    .glov-voice-chat-session-pill {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 9px 13px;
      border-radius: 999px;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(18,17,17,0.84);
      backdrop-filter: blur(10px);
      color: rgba(255,255,255,0.72);
      box-sizing: border-box;
      transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
    }
    .glov-voice-chat-session-pill svg {
      flex-shrink: 0;
      color: rgba(255,255,255,0.5);
    }
    .glov-voice-chat-session-pill-text {
      font-size: 12px;
      line-height: 1.4;
      text-align: left;
    }
    .glov-voice-chat-session-note[data-tone='success'] .glov-voice-chat-session-pill {
      background: rgba(22,163,74,0.16);
      border-color: rgba(74,222,128,0.28);
      color: #d8ffe5;
    }
    .glov-voice-chat-session-note[data-tone='success'] .glov-voice-chat-session-pill svg {
      color: #9ef0b6;
    }
    .glov-voice-chat-session-note[data-tone='error'] .glov-voice-chat-session-pill {
      background: rgba(239,68,68,0.14);
      border-color: rgba(248,113,113,0.28);
      color: #ffd7d3;
    }
    .glov-voice-chat-session-note[data-tone='error'] .glov-voice-chat-session-pill svg {
      color: #ffb4ad;
    }
    .glov-voice-chat-panel[data-state='connecting'] {
      background:
        radial-gradient(circle at 50% 16%, rgba(165,81,13,0.68), transparent 34%),
        radial-gradient(circle at 50% 28%, rgba(237,145,36,0.2), transparent 45%),
        linear-gradient(180deg, #321507 0%, #140904 34%, #050404 70%, #020202 100%);
    }
    .glov-voice-chat-panel[data-state='connecting'] .glov-voice-chat-ring,
    .glov-voice-chat-panel[data-state='speaking'] .glov-voice-chat-ring,
    .glov-voice-chat-panel[data-state='listening'] .glov-voice-chat-ring {
      opacity: 1;
      animation-play-state: running;
    }
    .glov-voice-chat-panel[data-state='connecting'] .glov-voice-chat-ring {
      animation-duration: 4.8s;
      border-color: rgba(255,184,92,0.46);
    }
    .glov-voice-chat-panel[data-state='connecting'] .glov-voice-chat-avatar {
      box-shadow:
        0 0 0 1px rgba(255,255,255,0.28),
        0 0 46px rgba(235,156,55,0.48),
        0 16px 40px rgba(0,0,0,0.32);
    }
    .glov-voice-chat-panel[data-state='speaking'] .glov-voice-chat-ring {
      animation-duration: 3.1s;
      border-color: rgba(255,196,104,0.52);
    }
    .glov-voice-chat-panel[data-state='speaking'] .glov-voice-chat-avatar {
      box-shadow:
        0 0 0 1px rgba(255,255,255,0.28),
        0 0 58px rgba(245,158,11,0.52),
        0 18px 42px rgba(0,0,0,0.34);
    }
    .glov-voice-chat-panel[data-state='listening'] .glov-voice-chat-ring {
      animation-duration: 4.4s;
      border-color: rgba(255,213,150,0.48);
    }
    .glov-voice-chat-panel[data-state='listening'] .glov-voice-chat-avatar {
      box-shadow:
        0 0 0 1px rgba(255,255,255,0.28),
        0 0 48px rgba(255,255,255,0.14),
        0 16px 40px rgba(0,0,0,0.32);
    }
    .glov-voice-chat-panel[data-state='transferring'] .glov-voice-chat-avatar,
    .glov-voice-chat-panel[data-state='waiting_sms'] .glov-voice-chat-avatar,
    .glov-voice-chat-panel[data-state='saving'] .glov-voice-chat-avatar {
      animation-duration: 3.8s;
      box-shadow:
        0 0 0 1px rgba(255,255,255,0.22),
        0 0 34px rgba(245,158,11,0.3),
        0 16px 40px rgba(0,0,0,0.32);
    }
    .glov-voice-chat-panel[data-state='error'] {
      background:
        radial-gradient(circle at top, rgba(239,68,68,0.18), transparent 42%),
        linear-gradient(180deg, #140808 0%, #070707 100%);
    }
    .glov-voice-chat-panel[data-state='error'] .glov-voice-chat-avatar {
      border-color: rgba(248,113,113,0.9);
      box-shadow:
        0 0 32px rgba(239,68,68,0.34),
        0 16px 40px rgba(0,0,0,0.32);
    }
    .glov-voice-chat-panel[data-state='error'] .glov-voice-chat-ring {
      opacity: 0;
      animation-play-state: paused;
    }
    @keyframes glov-avatar-breathe {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.04); opacity: 0.96; }
    }
    @keyframes glov-live-pulse {
      0%, 100% { box-shadow: 0 0 0 1px rgba(74,222,128,0.22); }
      50% { box-shadow: 0 0 0 4px rgba(74,222,128,0.12); }
    }
    @keyframes glov-ring-ripple {
      0% { transform: scale(0.78); opacity: 0.82; }
      72% { opacity: 0.34; }
      100% { transform: scale(1.34); opacity: 0; }
    }
    @media (min-width: 769px) {
      .glov-voice-chat-container:not(.glov-voice-chat-mobile) {
        max-width: min(100vw - 48px, 280px);
      }
      .glov-voice-chat-container:not(.glov-voice-chat-mobile) .glov-voice-chat-call-button {
        flex: 1 1 auto;
        min-width: 0;
      }
    }
    @media (max-width: 768px) {
      .glov-voice-chat-container {
        min-width: auto; max-width: min(100vw - 20px, 370px);
        bottom: calc(max(12px, env(safe-area-inset-bottom, 0px)) + 72px);
        right: max(10px, env(safe-area-inset-right, 0px));
      }
      .glov-voice-chat-container.glov-voice-chat-mobile .glov-voice-chat-container-inner {
        height: 64px;
        padding: 6px 8px;
        gap: 10px;
        border-radius: 999px;
      }
      .glov-voice-chat-container.glov-voice-chat-mobile .glov-voice-chat-intents {
        display: none;
      }
      .glov-voice-chat-container.glov-voice-chat-mobile .glov-voice-chat-call-button {
        flex: 1 1 auto;
        min-width: 0;
        min-height: 44px;
        padding: 8px 15px;
        gap: 8px;
        font-size: 14px;
      }
      .glov-voice-chat-container.glov-voice-chat-mobile .glov-voice-chat-btn-avatar {
        width: 40px;
        height: 40px;
      }
      .glov-voice-chat-container.glov-voice-chat-mobile .glov-voice-chat-btn-live-dot {
        width: 12px;
        height: 12px;
      }
      .glov-voice-chat-container.glov-voice-chat-mobile .glov-voice-chat-open-chat-btn {
        display: inline-flex;
        width: 52px;
        min-width: 52px;
        height: 52px;
        border-radius: 999px;
      }
      .glov-voice-chat-container.glov-voice-chat-mobile .glov-voice-chat-open-chat-icon {
        width: 23px;
        height: 23px;
      }
      .glov-voice-chat-panel-content {
        gap: 12px;
        padding:
          calc(22px + env(safe-area-inset-top, 0px))
          24px
          calc(26px + env(safe-area-inset-bottom, 0px));
      }
      .glov-voice-chat-main {
        gap: 8px;
        padding-top: clamp(8px, 2.8vh, 24px);
      }
      .glov-voice-chat-visual {
        width: min(88vw, 340px);
        height: min(88vw, 340px);
      }
      .glov-voice-chat-control-button {
        width: 64px;
        height: 64px;
      }
      .glov-voice-chat-panel-controls {
        gap: 18px;
      }
      .glov-voice-chat-session-pill {
        max-width: 100%;
      }
      .glov-voice-chat-info-card {
        min-height: 68px;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .glov-voice-chat-btn-live-dot,
      .glov-voice-chat-avatar,
      .glov-voice-chat-ring,
      .glov-voice-chat-control-button,
      .glov-voice-chat-panel,
      .glov-voice-chat-container-inner,
      .glov-voice-chat-call-button,
      .glov-voice-chat-open-chat-btn {
        animation: none !important;
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}

function isMobile(): boolean {
  return window.innerWidth <= 768;
}

function startMockSession({
  onConnect,
  onDisconnect,
  onModeChange,
  onMessage,
}: SessionCallbacks): VoiceSession {
  const conversation_id = `mock-conv-${Date.now()}`;
  const timers: number[] = [];
  let ended = false;

  const schedule = (delay: number, fn: () => void) => {
    const timer = window.setTimeout(() => {
      if (!ended) fn();
    }, delay);
    timers.push(timer);
  };

  schedule(50, () => onConnect?.());
  schedule(280, () => onModeChange?.({ mode: 'listening' }));
  schedule(1200, () => onModeChange?.({ mode: 'speaking' }));
  schedule(2200, () => onModeChange?.({ mode: 'listening' }));
  schedule(360, () =>
    onMessage?.({
      role: 'assistant',
      source: 'mock',
      message: 'Merhaba, test sürüşü için telefon numaranızı alabilir miyim?',
    })
  );
  schedule(1680, () =>
    onMessage?.({
      role: 'user',
      source: 'mock',
      message: 'Tabii, numaram 05321234567.',
    })
  );

  return {
    conversation_id,
    async endSession() {
      if (ended) return;
      ended = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      onDisconnect?.();
    },
  };
}

export function mountVoiceLauncher({
  middlewareUrl,
  onOpenChat,
}: VoiceLauncherOptions): () => void {
  ensureStyles();
  const elevenLabsSdkUrl = resolveElevenLabsBrowserSdkUrl(middlewareUrl, window.location.href);

  const container = document.createElement('div');
  container.className = 'glov-voice-chat-container';
  container.innerHTML = `
    <div class="glov-voice-chat-intents" data-testid="voice-launcher-intents" aria-label="Hızlı sohbet seçenekleri">
      ${DESKTOP_INTENT_ACTIONS.map(
        (action) => `
          <button class="glov-voice-chat-intent-btn" data-testid="voice-launcher-intent-button" type="button" data-message="${action.message}">
            ${action.label}
          </button>
        `
      ).join('')}
    </div>
    <button class="glov-voice-chat-otp-retry" data-testid="voice-otp-retry-button"
      type="button" hidden>
      SMS doğrulamaya devam et
    </button>
    <div class="glov-voice-chat-container-inner">
      <span class="glov-voice-chat-btn-avatar-wrap" aria-hidden="true">
        <img class="glov-voice-chat-btn-avatar"
          src="${OTOKOC_LAUNCHER_AVATAR_URL}" alt="" />
        <span class="glov-voice-chat-btn-live-dot"></span>
      </span>
      <span class="glov-voice-chat-actions">
        <button class="glov-voice-chat-open-chat-btn" id="glov-open-chat-btn" data-testid="voice-launcher-open-button" type="button" aria-label="Sesli görüşmeyi başlat">
          <svg class="glov-voice-chat-open-chat-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
            fill="currentColor" aria-hidden="true">
            <path d="M6.62 10.79c1.44 2.82 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"></path>
          </svg>
        </button>
        <button class="glov-voice-chat-call-button" id="glov-call-agent-btn" data-testid="voice-launcher-chat-button" type="button" aria-label="Yazılı sohbeti aç">
          <svg class="glov-voice-chat-call-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <span class="glov-voice-chat-call-label">Uzmanla Görüş</span>
        </button>
      </span>
    </div>

    <div class="glov-voice-chat-panel" data-testid="voice-launcher-panel" data-state="idle" style="display:none">
      <div class="glov-voice-chat-panel-hit-layer" aria-hidden="true"></div>
      <div class="glov-voice-chat-panel-content">
        <div class="glov-voice-chat-panel-header">
          <div class="glov-voice-chat-panel-kicker">OTOKOÇ AI CANLI SES</div>
        </div>
        <div class="glov-voice-chat-main">
          <div class="glov-voice-chat-visual" aria-hidden="true">
            <div class="glov-voice-chat-rings">
              <div class="glov-voice-chat-ring"></div>
              <div class="glov-voice-chat-ring"></div>
              <div class="glov-voice-chat-ring"></div>
              <div class="glov-voice-chat-ring"></div>
            </div>
            <div class="glov-voice-chat-avatar">
              <img class="glov-voice-chat-avatar-img"
                src="${OTOKOC_LAUNCHER_AVATAR_URL}" alt="" />
            </div>
          </div>
          <div class="glov-voice-chat-state">
            <div class="glov-voice-chat-status-meta">
              <div class="glov-voice-chat-state-headline" id="glov-voice-badge" data-testid="voice-launcher-badge">
                Hazır
              </div>
              <div class="glov-voice-chat-duration" id="glov-voice-duration" data-visible="false">
                00:00
              </div>
            </div>
            <p class="glov-voice-chat-status-copy" id="glov-voice-copy" data-testid="voice-launcher-copy" data-empty="false">
              Başlatmak için butona dokunun.
            </p>
          </div>
        </div>
        <div class="glov-voice-chat-panel-footer">
          <div class="glov-voice-chat-info-stack" aria-hidden="true">
            <div class="glov-voice-chat-info-card">
              <span class="glov-voice-chat-info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="m9 12 2 2 4-4"/>
                </svg>
              </span>
              <span class="glov-voice-chat-info-text">
                <strong class="glov-voice-chat-info-title" id="glov-voice-info-primary-title">Bağlantı kuruluyor</strong>
                <span class="glov-voice-chat-info-detail" id="glov-voice-info-primary-detail">Mikrofon ve ses oturumu hazırlanıyor.</span>
              </span>
            </div>
            <div class="glov-voice-chat-info-card">
              <span class="glov-voice-chat-info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="27" height="27"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="9"/>
                  <path d="M12 7v5l3 2"/>
                </svg>
              </span>
              <span class="glov-voice-chat-info-text">
                <strong class="glov-voice-chat-info-title" id="glov-voice-info-secondary-title">Asistan aranıyor</strong>
                <span class="glov-voice-chat-info-detail" id="glov-voice-info-secondary-detail">Cevap verince konuşma başlayacak.</span>
              </span>
            </div>
          </div>
          <div class="glov-voice-otp-host" data-testid="voice-otp-host"></div>
          <div class="glov-voice-chat-session-note" id="glov-voice-session-note"
            data-testid="voice-launcher-session-note" data-tone="neutral">
            <div class="glov-voice-chat-session-pill">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span class="glov-voice-chat-session-pill-text" id="glov-voice-session-note-text">
                Konuşma kayıt altına alınır.
              </span>
            </div>
          </div>
          <div class="glov-voice-chat-panel-controls">
            <div class="glov-voice-chat-control">
              <button class="glov-voice-chat-control-button glov-voice-chat-control-button-secondary"
                id="glov-voice-microphone" type="button" aria-label="Mikrofon" aria-pressed="false">
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <path d="M12 19v3"/>
                </svg>
              </button>
              <span class="glov-voice-chat-control-label">Mikrofon</span>
            </div>
            <div class="glov-voice-chat-control">
              <button class="glov-voice-chat-control-button glov-voice-chat-control-button-primary"
                id="glov-voice-end" data-testid="voice-launcher-end-button" type="button"
                aria-label="Görüşmeyi bitir">
                <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34"
                  viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.82 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"></path>
                </svg>
              </button>
              <span class="glov-voice-chat-control-label">Bitir</span>
            </div>
            <div class="glov-voice-chat-control">
              <button class="glov-voice-chat-control-button glov-voice-chat-control-button-secondary"
                id="glov-voice-speaker" type="button" aria-label="Hoparlör" aria-pressed="false">
                <svg xmlns="http://www.w3.org/2000/svg" width="31" height="31"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 5 6 9H3v6h3l5 4V5z"/>
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                </svg>
              </button>
              <span class="glov-voice-chat-control-label">Hoparlör</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(container);

  // ── Refs ──────────────────────────────────────────────────────────────
  const callButton = container.querySelector('#glov-call-agent-btn') as HTMLButtonElement;
  const callLabel = container.querySelector('.glov-voice-chat-call-label') as HTMLElement;
  const intentButtons = Array.from(
    container.querySelectorAll<HTMLButtonElement>('.glov-voice-chat-intent-btn')
  );
  const otpRetryButton = container.querySelector(
    '[data-testid="voice-otp-retry-button"]'
  ) as HTMLButtonElement;
  const panel = container.querySelector('.glov-voice-chat-panel') as HTMLElement;
  const innerBar = container.querySelector('.glov-voice-chat-container-inner') as HTMLElement;
  const openChatBtn = container.querySelector('#glov-open-chat-btn') as HTMLButtonElement;
  const microphoneBtn = container.querySelector('#glov-voice-microphone') as HTMLButtonElement;
  const speakerBtn = container.querySelector('#glov-voice-speaker') as HTMLButtonElement;
  const endBtn = container.querySelector('#glov-voice-end') as HTMLButtonElement;
  const badge = container.querySelector('#glov-voice-badge') as HTMLElement;
  const copy = container.querySelector('#glov-voice-copy') as HTMLElement;
  const sessionNote = container.querySelector('#glov-voice-session-note') as HTMLElement;
  const sessionNoteText = container.querySelector('#glov-voice-session-note-text') as HTMLElement;
  const duration = container.querySelector('#glov-voice-duration') as HTMLElement;
  const infoPrimaryTitle = container.querySelector('#glov-voice-info-primary-title') as HTMLElement;
  const infoPrimaryDetail = container.querySelector(
    '#glov-voice-info-primary-detail'
  ) as HTMLElement;
  const infoSecondaryTitle = container.querySelector(
    '#glov-voice-info-secondary-title'
  ) as HTMLElement;
  const infoSecondaryDetail = container.querySelector(
    '#glov-voice-info-secondary-detail'
  ) as HTMLElement;
  const inlineOtpHost = container.querySelector('[data-testid="voice-otp-host"]') as HTMLElement;

  // ── State ──────────────────────────────────────────────────────────────
  let conversation: VoiceSession | null = null;
  let sessionAttemptId = 0;
  let cachedAgentId: string | null = null;
  let useMockSession = false;
  let voiceSessionSecret: string | undefined;
  let voiceSessionUserId: string | undefined;
  let manualShutdown = false;
  let currentConversationId: string | null = null;
  let lastSavedConversationId: string | null = null;
  let lastOtpConversationId: string | null = null;
  let lastOtpCloseReason: VoiceOtpCloseReason = null;
  let voiceOtpOverlayOpen = false;
  let voiceOtpRetry: VoiceOtpRetry | null = null;
  let voiceTranscript = '';
  let userSpeechCount = 0;
  let recentUserUtterances: string[] = [];
  let leadContextUserUtterances: string[] = [];
  let otpHandoffPending = false;
  let otpHandoffSignal: OtpHandoffSignal | null = null;
  let otpTransferStarted = false;
  let otpFallbackTimer: number | null = null;
  let isAssistantSpeaking = false;
  let pendingSavePromise: Promise<void> | null = null;
  let voiceBookingToolState = createEmptyVoiceBookingToolState();
  let pendingVoiceBrandModel: string | null = null;
  let pendingValidationTrace: { traceRevision: number } | null = null;
  let callConnectedAt: number | null = null;
  let durationTimer: number | null = null;
  let listeningFallbackTimer: number | null = null;
  let otpVerified = false;
  let voiceConfirmationMessage = '';
  let smsHandoffAnnounced = false;
  let nativeSmsWait: Promise<string> | null = null;
  let resolveNativeSmsWait: ((result: string) => void) | null = null;
  let nativeSmsTimer: number | null = null;
  let otpHolding = false;
  let verifiedCloseSessionKey: string | null = null;
  let verifiedCloseSawAssistantSpeech = false;
  let verifiedCloseFallbackTimer: number | null = null;
  let ringbackAudioContext: AudioContext | null = null;
  let ringbackTimer: number | null = null;
  let ringbackActive = false;
  const clientToolDiagnostics = new VoiceClientToolDiagnostics();

  const CALL_ICON_SVG = `
    <svg class="glov-voice-chat-open-chat-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
      fill="currentColor" aria-hidden="true">
      <path d="M6.62 10.79c1.44 2.82 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"></path>
    </svg>
  `;

  function enforceVoiceCallIcon() {
    if (openChatBtn.querySelector('img')) {
      openChatBtn.querySelectorAll('img').forEach((img) => img.remove());
    }
    if (!openChatBtn.querySelector('.glov-voice-chat-open-chat-icon')) {
      openChatBtn.innerHTML = CALL_ICON_SVG;
    }
  }

  function formatDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${mm}:${ss}`;
    }
    return `${mm}:${ss}`;
  }

  function renderDuration() {
    if (callConnectedAt === null) {
      duration.textContent = '00:00';
      return;
    }
    const seconds = Math.max(0, Math.floor((Date.now() - callConnectedAt) / 1000));
    duration.textContent = formatDuration(seconds);
  }

  function startDurationTimer() {
    if (callConnectedAt === null) {
      callConnectedAt = Date.now();
    }
    renderDuration();
    if (durationTimer !== null) {
      return;
    }
    durationTimer = window.setInterval(() => {
      renderDuration();
    }, 1000);
  }

  function stopDurationTimer(reset = true) {
    if (durationTimer !== null) {
      window.clearInterval(durationTimer);
      durationTimer = null;
    }
    if (reset) {
      callConnectedAt = null;
      duration.textContent = '00:00';
    }
  }

  function getCurrentPanelState(): VoicePanelState {
    const state = panel.getAttribute('data-state') as VoicePanelState | null;
    return state || 'idle';
  }

  function clearListeningFallback() {
    if (listeningFallbackTimer !== null) {
      window.clearTimeout(listeningFallbackTimer);
      listeningFallbackTimer = null;
    }
  }

  function scheduleListeningFallback(attemptId: number) {
    clearListeningFallback();
    listeningFallbackTimer = window.setTimeout(() => {
      listeningFallbackTimer = null;
      if (
        attemptId !== sessionAttemptId ||
        !conversation ||
        otpHandoffPending ||
        getCurrentPanelState() !== 'speaking'
      ) {
        return;
      }
      isAssistantSpeaking = false;
      setPanelState('listening');
      setSessionNote(DEFAULT_SESSION_NOTE, 'neutral');
    }, 3600);
  }

  function getRingbackAudioContext(): AudioContext | null {
    if (ringbackAudioContext) {
      return ringbackAudioContext;
    }
    const audioWindow = window as Window & {
      webkitAudioContext?: typeof AudioContext;
    };
    const AudioContextConstructor = window.AudioContext || audioWindow.webkitAudioContext;
    if (!AudioContextConstructor) {
      return null;
    }
    ringbackAudioContext = new AudioContextConstructor();
    return ringbackAudioContext;
  }

  function playRingbackPulse() {
    if (!ringbackActive) {
      return;
    }
    const audioContext = getRingbackAudioContext();
    if (!audioContext) {
      return;
    }

    void audioContext.resume().catch(() => {});

    const firstTone = audioContext.createOscillator();
    const secondTone = audioContext.createOscillator();
    const gain = audioContext.createGain();
    const now = audioContext.currentTime;
    const end = now + 1.05;

    firstTone.type = 'sine';
    firstTone.frequency.setValueAtTime(425, now);
    secondTone.type = 'sine';
    secondTone.frequency.setValueAtTime(475, now);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.055, now + 0.08);
    gain.gain.setValueAtTime(0.055, now + 0.84);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    firstTone.connect(gain);
    secondTone.connect(gain);
    gain.connect(audioContext.destination);
    firstTone.start(now);
    secondTone.start(now);
    firstTone.stop(end);
    secondTone.stop(end);

    ringbackTimer = window.setTimeout(() => {
      ringbackTimer = null;
      playRingbackPulse();
    }, 3100);
  }

  function startRingbackTone() {
    if (ringbackActive) {
      return;
    }
    ringbackActive = true;
    playRingbackPulse();
  }

  function stopRingbackTone() {
    ringbackActive = false;
    if (ringbackTimer !== null) {
      window.clearTimeout(ringbackTimer);
      ringbackTimer = null;
    }
  }

  function buildVoiceLeadConfirmationMessage(now = new Date()): string {
    const hour = Number(
      new Intl.DateTimeFormat('tr-TR', {
        timeZone: 'Europe/Istanbul',
        hour: 'numeric',
        hourCycle: 'h23',
      }).format(now)
    );
    const farewell =
      hour < 6 || hour >= 22 ? 'İyi geceler' : hour >= 18 ? 'İyi akşamlar' : 'İyi günler';
    return `${getLeadConfirmationMessage(voiceBookingToolState.intent || undefined)} ${farewell}.`;
  }

  function resolveBookingInfoCards(): VoiceInfoCards {
    const intent = voiceBookingToolState.intent;
    if (!intent) {
      return {
        primaryTitle: 'Talebiniz alınıyor',
        primaryDetail: 'Sizi dinleyerek arama nedeninizi netleştiriyoruz.',
        secondaryTitle: 'Arama nedeniniz belirleniyor',
        secondaryDetail: 'Servis, test sürüşü veya bilgi talebi ayrıştırılıyor.',
      };
    }

    const intentLabel = getVoiceBookingIntentLabel(intent);
    if (voiceBookingToolState.readyForOtp) {
      return {
        primaryTitle: `${intentLabel} tamamlandı`,
        primaryDetail: 'Gerekli bilgiler alındı.',
        secondaryTitle: 'SMS adımı hazırlanıyor',
        secondaryDetail: 'Doğrulama ekranına geçiş yapılacak.',
      };
    }

    const nextField = voiceBookingToolState.nextField || voiceBookingToolState.missingFields[0];
    const fieldTitle = nextField
      ? VOICE_BOOKING_FIELD_PROGRESS_TITLES[nextField] ||
        `${VOICE_BOOKING_FIELD_LABELS[nextField] || 'Bilgi'} doğrulanıyor`
      : 'Bilgiler doğrulanıyor';
    const missingCount = voiceBookingToolState.missingFields.length;
    const secondaryDetail =
      missingCount > 0
        ? `${missingCount} zorunlu bilgi daha tamamlanacak.`
        : 'Alınan bilgiler kontrol ediliyor.';

    return {
      primaryTitle: fieldTitle,
      primaryDetail: `${intentLabel} için gerekli bilgiler alınıyor.`,
      secondaryTitle: `${intentLabel} alınıyor`,
      secondaryDetail,
    };
  }

  function resolveVoiceInfoCards(state: VoicePanelState): VoiceInfoCards {
    if (state === 'speaking' || state === 'listening') {
      return resolveBookingInfoCards();
    }
    return STATE_INFO_CARDS[state];
  }

  function renderInfoCards(state: VoicePanelState) {
    const infoCards = resolveVoiceInfoCards(state);
    infoPrimaryTitle.textContent = infoCards.primaryTitle;
    infoPrimaryDetail.textContent = infoCards.primaryDetail;
    infoSecondaryTitle.textContent = infoCards.secondaryTitle;
    infoSecondaryDetail.textContent = infoCards.secondaryDetail;
  }

  function setPanelState(state: VoicePanelState) {
    const { headline, detail, durationVisible } = STATE_LABELS[state];
    if (state !== 'speaking') {
      clearListeningFallback();
    }
    if (state === 'connecting') {
      startRingbackTone();
    } else {
      stopRingbackTone();
    }
    badge.textContent = headline;
    copy.textContent = detail;
    copy.dataset.empty = detail ? 'false' : 'true';
    duration.dataset.visible = durationVisible ? 'true' : 'false';
    renderInfoCards(state);
    panel.setAttribute('data-state', state);
  }

  function setSessionNote(text: string, tone: 'neutral' | 'success' | 'error' = 'neutral') {
    sessionNoteText.textContent = text;
    sessionNote.dataset.tone = tone;
  }

  function traceVoiceState(event: string, extra: Record<string, unknown> = {}): void {
    void event;
    void extra;
  }

  function postVoiceTrace(payload: VoiceTracePayload): void {
    void fetch(`${middlewareUrl}/agents/trace`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        clientSdkVersion: ELEVENLABS_BROWSER_SDK_VERSION,
      }),
      keepalive: true,
    }).catch(() => {});
  }

  function summarizeTraceMessage(message: string): string {
    const normalized = message.replace(/\s+/g, ' ').trim();
    return normalized.length <= 120 ? normalized : `${normalized.slice(0, 117)}...`;
  }

  function postVoiceConversationMessageTrace(message: {
    message?: string;
    role?: string;
    source?: string;
  }): void {
    if (typeof message.message !== 'string' || !message.message.trim()) {
      return;
    }
    postVoiceTrace({
      event: 'message',
      conversationId: currentConversationId,
      otpHandoffPending,
      handoffSignal: otpHandoffSignal,
      role: message.role ?? null,
      source: message.source ?? null,
      snippet: summarizeTraceMessage(message.message),
      bookingIntent: voiceBookingToolState.intent,
      requiredFields: voiceBookingToolState.requiredFields,
      missingFields: voiceBookingToolState.missingFields,
      nextField: voiceBookingToolState.nextField,
      readyForOtp: voiceBookingToolState.readyForOtp,
      validatedFields: voiceBookingToolState.fields,
    });
  }

  function getConversationId(targetConversation: VoiceSession | null): string | null {
    const directId = targetConversation?.conversation_id;
    if (typeof directId === 'string' && directId.length > 0) {
      return directId;
    }

    const getter = targetConversation?.getId;
    if (typeof getter === 'function') {
      const value = getter.call(targetConversation);
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }

    return null;
  }

  function readVoiceBookingIntent(value: unknown): VoiceBookingIntent | undefined {
    return value === 'technical_service' ||
      value === 'test_drive' ||
      value === 'general_info' ||
      value === 'service'
      ? value
      : undefined;
  }

  function readVoiceBookingText(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  function readVoiceBookingFields(value: unknown): VoiceBookingFields {
    const source =
      value && typeof value === 'object'
        ? (value as Record<string, unknown>)
        : ({} as Record<string, unknown>);
    return {
      full_name: readVoiceBookingText(source.full_name),
      phone: readVoiceBookingText(source.phone),
      email: readVoiceBookingText(source.email),
      brand_model: readVoiceBookingText(source.brand_model),
      brand_model_declined: source.brand_model_declined === true || undefined,
      city: readVoiceBookingText(source.city),
      location_id: readVoiceBookingText(source.location_id),
      preferred_datetime: readVoiceBookingText(source.preferred_datetime),
      pending_datetime_raw_text: readVoiceBookingText(source.pending_datetime_raw_text),
      pending_datetime_status: readVoiceBookingPendingStatus(source.pending_datetime_status),
      pending_datetime_reason: readVoiceBookingPendingReason(source.pending_datetime_reason),
      request_subject: readVoiceBookingText(source.request_subject),
    };
  }

  function readVoiceBookingPendingStatus(
    value: unknown
  ): VoiceBookingFields['pending_datetime_status'] | undefined {
    return value === 'absent' || value === 'pending' || value === 'resolved' ? value : undefined;
  }

  function readVoiceBookingPendingReason(
    value: unknown
  ): VoiceBookingFields['pending_datetime_reason'] | undefined {
    return value === 'missing_location_context' ||
      value === 'missing_time' ||
      value === 'missing_date' ||
      value === 'unclear'
      ? value
      : undefined;
  }

  function buildVoiceBookingGuidance(result: VoiceBookingValidationResult): string {
    const fieldMessages = result.field_messages || {};
    const acceptedFields = result.fields || {};
    const priorityMessage =
      fieldMessages.full_name ||
      fieldMessages.phone ||
      fieldMessages.email ||
      fieldMessages.brand_model ||
      fieldMessages.location_id ||
      fieldMessages.preferred_datetime;

    if (priorityMessage) {
      return priorityMessage;
    }

    if (result.ready_for_otp) {
      return 'All required booking details are validated. You may call start_sms_verification now.';
    }

    const nextField = result.next_field;
    const locationContext = result.location_context;
    if (nextField === 'location_id' && locationContext?.city_selected !== true) {
      return result.intent === 'general_info'
        ? "Ask only for the caller's current or living city now before asking for an exact sales branch for the information request. Do not read or offer city_options, even if the caller says they have no preference."
        : "Brand/model is already handled if it appears in accepted_fields. Ask only for the caller's current or living city now before asking for an exact branch. Do not read or offer city_options, even if the caller says they have no preference.";
    }
    if (nextField === 'location_id' && locationContext?.branch_options_ready === true) {
      return result.intent === 'general_info'
        ? 'Ask only for one exact sales branch now for the information request. Do not read branch_options unless the caller explicitly asks or needs help choosing; if you list options, mention at most 3.'
        : 'Brand/model and city are already handled if they appear in accepted_fields. Ask only for one exact branch now. Do not read branch_options unless the caller explicitly asks or needs help choosing; if you list options, mention at most 3.';
    }
    if (nextField === 'preferred_datetime') {
      return 'Brand/model and exact branch are already validated if they appear in accepted_fields. Ask only for the preferred date and time now.';
    }
    if (nextField === 'brand_model') {
      return result.intent === 'general_info'
        ? 'Ask only which brand and model the caller wants information about next. If the caller declines or says it does not matter, call validate_booking_candidate with brand_model_declined=true and do not ask brand_model again.'
        : 'Ask only for the exact brand and model next. Do not ask for city or branch yet.';
    }
    if (nextField === 'full_name') {
      return "Ask only for the caller's full name and surname next.";
    }
    if (nextField === 'phone') {
      return "Ask only for the caller's phone number next. Do not repeat the digits back.";
    }
    if (nextField === 'email') {
      return "Ask only for the caller's email address next.";
    }
    if (nextField === 'request_subject') {
      return "Ask only for the caller's request topic next.";
    }

    if (acceptedFields.brand_model || acceptedFields.city || acceptedFields.location_id) {
      return 'Use accepted_fields as already completed details and continue with only the next missing validated booking detail.';
    }

    return 'Continue with only the next missing validated booking detail.';
  }

  function summarizeVoiceBookingValidation(result: VoiceBookingValidationResult): string {
    return JSON.stringify({
      intent: result.intent,
      required_fields: result.required_fields || [],
      accepted_fields: result.fields || {},
      missing_fields: result.missing_fields || [],
      next_field: result.next_field || null,
      ready_for_otp: result.ready_for_otp === true,
      field_messages: result.field_messages || {},
      city_options:
        result.next_field === 'location_id' && result.location_context?.city_selected === true
          ? (result.location_context?.eligible_cities || []).slice(0, 3)
          : [],
      branch_options: (result.location_context?.eligible_locations || [])
        .slice(0, 3)
        .map((item) => ({
          id: item.id,
          branch_name: item.branch_name,
          city: item.city,
        })),
      assistant_guidance: result.assistant_guidance || buildVoiceBookingGuidance(result),
      ...(result.next_question_suggestion
        ? { next_question_suggestion: result.next_question_suggestion }
        : {}),
      ...(result.response_policy ? { response_policy: result.response_policy } : {}),
    });
  }

  function isVoiceBookingValidationResult(value: unknown): value is VoiceBookingValidationResult {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const result = value as Record<string, unknown>;
    return (
      !!result.fields &&
      typeof result.fields === 'object' &&
      !Array.isArray(result.fields) &&
      Array.isArray(result.missing_fields) &&
      typeof result.ready_for_otp === 'boolean'
    );
  }

  async function requestVoiceBookingValidation(
    payload: Record<string, unknown>,
    sessionSignal: AbortSignal
  ): Promise<VoiceBookingValidationResult> {
    const result = await fetchVoiceClientToolJson(
      `${middlewareUrl}/agents/booking/validate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(voiceSessionSecret ? { Authorization: `Bearer ${voiceSessionSecret}` } : {}),
        },
        body: JSON.stringify(payload),
      },
      sessionSignal,
      VOICE_BOOKING_VALIDATION_TIMEOUT_MS
    );
    if (!isVoiceBookingValidationResult(result)) {
      throw new VoiceClientToolError({
        errorType: 'malformed_response',
        messageCode: 'booking_validate_malformed',
      });
    }
    return result;
  }

  async function validateVoiceBookingCandidateLive(
    args: Record<string, unknown> | undefined,
    signal: AbortSignal
  ): Promise<string> {
    const nestedFields =
      args?.fields && typeof args.fields === 'object' ? readVoiceBookingFields(args.fields) : {};
    const directFields = readVoiceBookingFields(args);
    const nextIntent = readVoiceBookingIntent(args?.intent) || voiceBookingToolState.intent;
    const mergedFields: VoiceBookingFields = {
      ...voiceBookingToolState.fields,
      ...nestedFields,
      ...directFields,
    };
    if (
      pendingVoiceBrandModel &&
      !mergedFields.brand_model_declined &&
      (!mergedFields.brand_model ||
        pendingVoiceBrandModel !== voiceBookingToolState.fields.brand_model)
    ) {
      mergedFields.brand_model = pendingVoiceBrandModel;
    }
    // Prefer the tool-provided utterance, then the latest user utterance —
    // never the whole transcript, which mixes assistant wording into the
    // backend's user-evidence checks (issues #115/#117).
    const fallbackText = resolveVoiceValidationFallbackText(
      readVoiceBookingText(args?.fallback_text),
      recentUserUtterances
    );
    const validationConversationId = currentConversationId ?? getConversationId(conversation);
    if (validationConversationId) {
      currentConversationId = validationConversationId;
    }

    traceVoiceState('booking-validate-request', {
      conversationId: validationConversationId,
      nextIntent,
      mergedFields,
      fallbackText,
    });

    const deviceContext = resolveOtokocDeviceContext();
    const result = await requestVoiceBookingValidation(
      {
        conversation_id: validationConversationId ?? undefined,
        intent: nextIntent,
        fields: mergedFields,
        fallback_text: fallbackText,
        recent_user_messages:
          recentUserUtterances.length > 0
            ? recentUserUtterances.map((text) => text.slice(0, VOICE_CONTEXT_MESSAGE_MAX_LENGTH))
            : undefined,
        lead_context_messages:
          leadContextUserUtterances.length > 0
            ? leadContextUserUtterances.map((text) =>
                text.slice(0, VOICE_CONTEXT_MESSAGE_MAX_LENGTH)
              )
            : undefined,
        ...deviceContext,
      },
      signal
    );
    // A language notice pauses the turn without clearing a previously validated SMS handoff.
    if (result.response_policy === 'language_notice') {
      return summarizeVoiceBookingValidation(result);
    }
    voiceBookingToolState = {
      traceRevision: result.trace_revision,
      validatedAt: result.validated_at,
      intent: readVoiceBookingIntent(result.intent),
      fields: result.fields || {},
      requiredFields: Array.isArray(result.required_fields) ? result.required_fields : [],
      nextField: result.next_field || null,
      missingFields: result.missing_fields || [],
      readyForOtp: result.ready_for_otp === true,
      referralGuidance:
        result.response_policy === 'redirect_without_lead' ? result.assistant_guidance : undefined,
    };
    if (!voiceBookingToolState.readyForOtp && otpHandoffPending) {
      otpHandoffPending = false;
      otpHandoffSignal = null;
      clearOtpFallbackTimer();
    }
    if (
      voiceBookingToolState.fields.brand_model ||
      voiceBookingToolState.fields.brand_model_declined
    ) {
      pendingVoiceBrandModel = null;
    }
    const currentPanelState = panel.getAttribute('data-state') as VoicePanelState | null;
    if (currentPanelState === 'speaking' || currentPanelState === 'listening') {
      renderInfoCards(currentPanelState);
    }

    traceVoiceState('booking-validate-response', {
      intent: result.intent,
      requiredFields: result.required_fields,
      fields: result.fields,
      missingFields: result.missing_fields,
      readyForOtp: result.ready_for_otp,
      fieldMessages: result.field_messages,
      traceRevision: result.trace_revision,
    });
    pendingValidationTrace =
      typeof result.trace_revision === 'number' && result.trace_revision > 0
        ? {
            traceRevision: result.trace_revision,
          }
        : null;
    postVoiceTrace({
      event: 'booking_validate',
      conversationId: currentConversationId,
      otpHandoffPending,
      handoffSignal: otpHandoffSignal,
      bookingIntent: readVoiceBookingIntent(result.intent),
      requiredFields: result.required_fields || [],
      missingFields: result.missing_fields || [],
      nextField: result.next_field || null,
      readyForOtp: result.ready_for_otp === true,
      validatedFields: result.fields || {},
      fieldMessages: result.field_messages || {},
      traceRevision: result.trace_revision,
      note: summarizeTraceMessage(summarizeVoiceBookingValidation(result)),
    });

    if (result.ready_for_otp && !otpVerified) {
      return summarizeVoiceBookingValidation({
        ...result,
        next_question_suggestion: undefined,
        assistant_guidance: smsHandoffAnnounced
          ? 'The SMS instruction was already spoken. Call wait_for_sms_verification now without repeating it or asking another question. Stay silent while waiting.'
          : `Say only: ${VOICE_SMS_HANDOFF_MESSAGE} Then call wait_for_sms_verification. Do not ask any more city or branch questions.`,
      });
    }

    return summarizeVoiceBookingValidation(result);
  }

  function getVoiceOtpOpenReason(): VoiceOtpOpenReason | null {
    return resolveVoiceOtpOpenReason({
      otpHandoffPending,
      handoffSignal: otpHandoffSignal,
      readyForOtp: voiceBookingToolState.readyForOtp,
    });
  }

  function setVoiceOtpRetry(retry: VoiceOtpRetry | null): void {
    voiceOtpRetry = retry;
    otpRetryButton.hidden = retry === null;
  }

  function resolveSmsWait(verified = false): void {
    if (nativeSmsTimer !== null) {
      window.clearTimeout(nativeSmsTimer);
      nativeSmsTimer = null;
    }
    resolveNativeSmsWait?.(
      JSON.stringify({
        verified,
        assistant_guidance: verified
          ? `Say only: ${voiceConfirmationMessage || buildVoiceLeadConfirmationMessage()} Do not ask another question.`
          : 'SMS verification was not completed. Do not claim completion or ask for a branch again.',
      })
    );
    resolveNativeSmsWait = null;
    nativeSmsWait = null;
  }

  function stopOtpHold(verified = false): void {
    resolveSmsWait(verified);
    if (otpHolding) {
      conversation?.setVolume?.({ volume: 1 });
    }
    if (voiceOtpOverlayOpen && !verified) {
      conversation?.setMicMuted?.(false);
    }
    otpHolding = false;
  }

  function clearVerifiedCloseFallback(): void {
    if (verifiedCloseFallbackTimer !== null) {
      window.clearTimeout(verifiedCloseFallbackTimer);
      verifiedCloseFallbackTimer = null;
    }
  }

  function finishVerifiedVoiceClose(sessionKey: string): void {
    if (verifiedCloseSessionKey !== sessionKey) {
      return;
    }
    clearVerifiedCloseFallback();
    verifiedCloseSessionKey = null;
    verifiedCloseSawAssistantSpeech = false;
    if (otpVerified && (!currentConversationId || currentConversationId === sessionKey)) {
      void endSession();
    }
  }

  function requestVerifiedVoiceClose(sessionKey: string): void {
    verifiedCloseSessionKey = sessionKey;
    verifiedCloseSawAssistantSpeech = false;
    clearVerifiedCloseFallback();
    verifiedCloseFallbackTimer = window.setTimeout(() => {
      finishVerifiedVoiceClose(sessionKey);
    }, VOICE_VERIFIED_CLOSE_FALLBACK_MS);
  }

  function trackVerifiedVoiceCloseMode(mode: 'speaking' | 'listening'): void {
    const sessionKey = verifiedCloseSessionKey;
    if (!sessionKey || (currentConversationId && currentConversationId !== sessionKey)) {
      return;
    }
    if (mode === 'speaking') {
      verifiedCloseSawAssistantSpeech = true;
      return;
    }
    if (verifiedCloseSawAssistantSpeech) {
      window.setTimeout(
        () => finishVerifiedVoiceClose(sessionKey),
        VOICE_VERIFIED_CLOSE_AFTER_LISTENING_MS
      );
    }
  }

  function startOtpHold(): void {
    if (otpHolding || !voiceOtpOverlayOpen || otpVerified) {
      return;
    }
    otpHolding = true;
    conversation?.setMicMuted?.(true);
    // Keep ElevenLabs output audible: the wait_for_sms_verification client tool
    // can use an ElevenLabs Elevator Music tool-call sound while it is pending.
    conversation?.setVolume?.({ volume: 1 });
    conversation?.sendUserActivity?.();
    setPanelState('waiting_sms');
    setSessionNote('SMS kodunu girip Kodu Doğrula düğmesine dokunun.', 'neutral');
  }

  function openVoiceOtpFlowOnce(sessionKey: string, phone: string | null): void {
    if (
      shouldSkipVoiceOtpOverlayOpen({
        lastConversationId: lastOtpConversationId,
        nextConversationId: sessionKey,
        overlayOpen: voiceOtpOverlayOpen,
        lastCloseReason: lastOtpCloseReason,
      })
    ) {
      traceVoiceState('otp-open-skipped-duplicate', { sessionKey, phone });
      return;
    }
    setVoiceOtpRetry(null);
    traceVoiceState('otp-open', { sessionKey, phone });
    panel.classList.add('glov-voice-chat-verifying');
    openUnifiedBookingOtpFlow({
      inlineTarget: inlineOtpHost,
      autoSend: false,
      initialPhone: phone ?? '',
      source: 'voice',
      threadId: sessionKey,
      readyDescription:
        'SMS doğrulamasını tamamladıktan sonra görüşmemize kaldığımız yerden devam edeceğiz.',
      closeOnVerified: true,
      onVerified: () => {
        voiceConfirmationMessage = buildVoiceLeadConfirmationMessage();
        otpVerified = true;
        stopOtpHold(true);
        conversation?.setMicMuted?.(true);
        conversation?.setVolume?.({ volume: 1 });
        setPanelState('saving');
        setSessionNote('Kaydediliyor...', 'neutral');
        void saveConversationIfNeeded(sessionKey);
        conversation?.sendContextualUpdate?.(
          `SMS doğrulaması tamamlandı. Türkçe olarak yalnızca şu kapanış cümlesini söyle ve başka soru sorma: ${voiceConfirmationMessage}`
        );
        conversation?.sendUserMessage?.('SMS doğrulamasını tamamladım.');
        requestVerifiedVoiceClose(sessionKey);
      },
      onClose: (reason) => {
        stopOtpHold(reason === 'verified');
        panel.classList.remove('glov-voice-chat-verifying');
        voiceOtpOverlayOpen = false;
        lastOtpCloseReason = reason;
        setVoiceOtpRetry(reason === 'dismiss' ? { sessionKey, phone } : null);
        traceVoiceState(reason === 'verified' ? 'otp-close-verified' : 'otp-close-dismissed', {
          sessionKey,
          phone,
        });
        if (reason !== 'verified') {
          closePanel();
        }
      },
    });
    lastOtpConversationId = sessionKey;
    lastOtpCloseReason = null;
    voiceOtpOverlayOpen = true;
    setPanelState('waiting_sms');
    setSessionNote('Telefon numaranızı kontrol edip SMS Gönder düğmesine basın.', 'neutral');
    conversation?.sendContextualUpdate?.(
      'SMS ekranı açıldı. Kullanıcı telefon numarasını kontrol edip gerekirse düzenleyecek, SMS Gönder düğmesine basacak ve gelen kodu girecek. Doğrulama bitene kadar sessizce bekle; şube, şehir veya başka bilgi sorma.'
    );
    startOtpHold();
  }

  function getVoiceOtpSessionKey(explicitConversationId?: string | null): string {
    return explicitConversationId || currentConversationId || `voice-live-${sessionAttemptId}`;
  }

  function clearOtpFallbackTimer(): void {
    if (otpFallbackTimer !== null) {
      window.clearTimeout(otpFallbackTimer);
      otpFallbackTimer = null;
    }
  }

  function markOtpHandoffPending(signal: OtpHandoffSignal): void {
    if (otpTransferStarted) {
      return;
    }
    if (!voiceBookingToolState.readyForOtp) {
      traceVoiceState('handoff-blocked-before-ready', {
        signal,
        missingFields: voiceBookingToolState.missingFields,
      });
      postVoiceTrace({
        event: 'handoff_blocked_before_ready',
        conversationId: currentConversationId,
        otpHandoffPending,
        handoffSignal: signal,
        bookingIntent: voiceBookingToolState.intent,
        missingFields: voiceBookingToolState.missingFields,
        readyForOtp: false,
        validatedFields: voiceBookingToolState.fields,
      });
      return;
    }
    if (otpHandoffPending && otpHandoffSignal === 'tool') {
      traceVoiceState('handoff-pending-preserved', { signal });
      return;
    }
    otpHandoffPending = true;
    otpHandoffSignal = signal;
    setPanelState('transferring');
    setSessionNote('SMS ekranı açılacak.', 'neutral');
    traceVoiceState('handoff-pending', { signal });
    postVoiceTrace({
      event: 'handoff_pending',
      conversationId: currentConversationId,
      otpHandoffPending: true,
      handoffSignal: signal,
      note: signal,
    });
    scheduleOtpFallback(VOICE_OTP_OPEN_AFTER_SPEECH_MS);
  }

  function startSmsVerificationTool(toolName: string): Promise<string> | string {
    if (voiceBookingToolState.referralGuidance) {
      return JSON.stringify({
        ok: false,
        assistant_guidance: voiceBookingToolState.referralGuidance,
      });
    }
    if (!voiceBookingToolState.readyForOtp) {
      traceVoiceState('otp-tool-blocked', {
        toolName,
        missingFields: voiceBookingToolState.missingFields,
        validatedFields: voiceBookingToolState.fields,
      });
      postVoiceTrace({
        event: 'otp_tool_blocked',
        conversationId: currentConversationId,
        otpHandoffPending,
        handoffSignal: otpHandoffSignal,
        bookingIntent: voiceBookingToolState.intent,
        missingFields: voiceBookingToolState.missingFields,
        readyForOtp: false,
        validatedFields: voiceBookingToolState.fields,
        note: `${toolName} blocked before validator readiness`,
      });
      return JSON.stringify({
        ok: false,
        assistant_guidance:
          'Booking is not ready for SMS approval yet. Do not mention an error or SMS. Continue with only the next missing validated field.',
        missing_fields: voiceBookingToolState.missingFields,
        accepted_fields: voiceBookingToolState.fields,
      });
    }
    postVoiceTrace({
      event: 'tool_called',
      conversationId: currentConversationId,
      otpHandoffPending,
      handoffSignal: 'tool',
      note: toolName,
    });
    markOtpHandoffPending('tool');
    return waitForSmsVerificationTool();
  }

  function waitForSmsVerificationTool(): Promise<string> | string {
    if (voiceBookingToolState.referralGuidance) {
      return JSON.stringify({
        verified: false,
        assistant_guidance: voiceBookingToolState.referralGuidance,
      });
    }
    if (!voiceBookingToolState.readyForOtp || otpVerified) {
      return JSON.stringify({
        verified: false,
        assistant_guidance:
          'Booking is not ready for SMS approval yet. Do not mention an error or SMS. Continue with only the next missing validated field.',
        missing_fields: voiceBookingToolState.missingFields,
        accepted_fields: voiceBookingToolState.fields,
      });
    }
    if (nativeSmsWait) {
      return nativeSmsWait;
    }
    nativeSmsWait = new Promise<string>((resolve) => {
      resolveNativeSmsWait = resolve;
    });
    nativeSmsTimer = window.setTimeout(() => {
      resolveSmsWait(false);
      setSessionNote(
        'SMS doğrulama süresi doldu. Kodu tekrar göndermeyi deneyebilirsiniz.',
        'error'
      );
    }, SMS_VERIFICATION_WAIT_TIMEOUT_MS);
    postVoiceTrace({
      event: 'tool_called',
      conversationId: currentConversationId,
      otpHandoffPending,
      handoffSignal: 'tool',
      note: 'wait_for_sms_verification',
    });
    markOtpHandoffPending('tool');
    scheduleOtpFallback(VOICE_OTP_OPEN_AFTER_SPEECH_MS);
    return nativeSmsWait;
  }

  function resolveVoiceOtpHandoff(
    reason: 'handoff' | 'disconnect' | 'manual_end',
    sessionKey: string,
    saveResponse: SaveConversationResponse | null = null
  ): { allowed: boolean; phone: string | null } {
    const openReason = getVoiceOtpOpenReason();
    const resolvedPhone = resolveOtpPhone(
      saveResponse,
      voiceTranscript,
      voiceBookingToolState.fields.phone
    );
    const allowed = !otpVerified && openReason !== null;

    traceVoiceState('otp-gate', {
      reason,
      sessionKey,
      phone: resolvedPhone,
      allowed,
      openReason,
    });
    postVoiceTrace({
      event: allowed ? 'otp_gate_open' : 'otp_gate_blocked',
      conversationId: sessionKey,
      otpHandoffPending,
      handoffSignal: otpHandoffSignal,
      phone: resolvedPhone,
      readyForOtp: voiceBookingToolState.readyForOtp,
      note: allowed && openReason ? `${reason}:${openReason}` : reason,
    });

    return {
      allowed,
      phone: resolvedPhone,
    };
  }

  function openResolvedVoiceOtpHandoff(
    sessionKey: string,
    otpHandoff: { allowed: boolean; phone: string | null }
  ): void {
    if (!otpHandoff.allowed) {
      return;
    }

    window.requestAnimationFrame(() => {
      openVoiceOtpFlowOnce(sessionKey, otpHandoff.phone);
      setSessionNote('SMS doğrulama ekranı açıldı.', 'neutral');
    });
  }

  async function triggerOtpTransfer(source: 'handoff' | 'disconnect'): Promise<void> {
    if (otpTransferStarted) {
      const conversationId = currentConversationId ?? getConversationId(conversation);
      postVoiceTrace({
        event: 'otp_transfer_skipped',
        conversationId: getVoiceOtpSessionKey(conversationId),
        otpHandoffPending,
        handoffSignal: otpHandoffSignal,
        note: source,
      });
      if (source === 'disconnect') {
        currentConversationId = conversationId;
        conversation = null;
        void saveConversationIfNeeded(conversationId);
      }
      return;
    }

    otpTransferStarted = true;
    clearOtpFallbackTimer();

    const activeConversation = conversation;
    const conversationId = currentConversationId ?? getConversationId(activeConversation);
    const sessionKey = getVoiceOtpSessionKey(conversationId);
    setPanelState('transferring');
    setSessionNote(
      'SMS ekranı açılıyor. Sonrasında görüşmemize kaldığımız yerden devam edeceğiz.',
      'neutral'
    );
    postVoiceTrace({
      event: 'otp_transfer_started',
      conversationId: sessionKey,
      otpHandoffPending,
      handoffSignal: otpHandoffSignal,
      phone: resolveOtpPhone(null, voiceTranscript, voiceBookingToolState.fields.phone),
      note: source,
    });
    const otpHandoff = resolveVoiceOtpHandoff(source, sessionKey);
    openResolvedVoiceOtpHandoff(sessionKey, otpHandoff);
    currentConversationId = conversationId;
    otpHandoffPending = false;
    otpHandoffSignal = null;
    isAssistantSpeaking = false;

    if (source === 'disconnect') {
      conversation = null;
      void saveConversationIfNeeded(conversationId, 'disconnect');
    }
  }

  function scheduleOtpFallback(delayMs: number): void {
    clearOtpFallbackTimer();
    otpFallbackTimer = window.setTimeout(() => {
      if (!otpHandoffPending) {
        traceVoiceState('otp-fallback-skipped', { reason: 'not_pending' });
        return;
      }
      if (isAssistantSpeaking && otpHandoffSignal !== 'tool') {
        traceVoiceState('otp-fallback-waiting', { reason: 'assistant_speaking' });
        scheduleOtpFallback(VOICE_OTP_SPEECH_RECHECK_MS);
        return;
      }
      traceVoiceState('otp-fallback-fired');
      void triggerOtpTransfer('handoff');
    }, delayMs);
  }

  async function saveConversationIfNeeded(
    conversationId: string | null,
    voiceEndReason?: VoiceEndReason
  ): Promise<SaveConversationResponse | null> {
    if (!conversationId) {
      traceVoiceState('save-skipped-missing-conversation-id');
      setSessionNote('Kayıt alınamadı.', 'error');
      postVoiceTrace({
        event: 'save_skipped_missing_conversation_id',
        conversationId: null,
      });
      return null;
    }

    if (lastSavedConversationId === conversationId) {
      traceVoiceState('save-skipped-already-saved', { conversationId });
      setSessionNote('Kaydedildi.', 'success');
      postVoiceTrace({
        event: 'save_skipped_already_saved',
        conversationId,
      });
      return null;
    }

    if (pendingSavePromise) {
      traceVoiceState('save-joined-pending-promise', { conversationId });
      await pendingSavePromise;
      return null;
    }

    setSessionNote('Kaydediliyor...', 'neutral');
    postVoiceTrace({
      event: 'save_started',
      conversationId,
      otpHandoffPending,
      handoffSignal: otpHandoffSignal,
    });

    let savedResponse: SaveConversationResponse | null = null;
    // Capture per-call state before a new session can reset these mutable fields.
    const saveSessionSecret = voiceSessionSecret;
    const bookingSnapshot = {
      traceRevision: voiceBookingToolState.traceRevision,
      validatedAt: voiceBookingToolState.validatedAt,
      intent: voiceBookingToolState.intent,
      fields: { ...voiceBookingToolState.fields },
    };
    pendingSavePromise = Promise.resolve()
      .then(() => {
        // A tool can resolve in the same event that triggers saving (notably
        // OTP verification). Let its instrumentation continuation commit first
        // while `pendingSavePromise` already protects save deduplication.
        const deviceContext: VoiceConversationSaveMetadata = {
          ...resolveOtokocDeviceContext(),
          clientSdkVersion: ELEVENLABS_BROWSER_SDK_VERSION,
          clientToolDiagnostics: clientToolDiagnostics.snapshot(),
          ...(voiceEndReason ? { voiceEndReason } : {}),
          userSpeechCount,
        };
        return fetch(`${middlewareUrl}/agents/conversations/${conversationId}/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(saveSessionSecret ? { Authorization: `Bearer ${saveSessionSecret}` } : {}),
          },
          body: JSON.stringify({ ...deviceContext, bookingSnapshot }),
        });
      })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Save failed: ${response.status}`);
        }
        const data = (await response.json()) as SaveConversationResponse;
        savedResponse = data;
        lastSavedConversationId = conversationId;
        const otpPhone = resolveOtpPhone(data, voiceTranscript, voiceBookingToolState.fields.phone);
        postVoiceTrace({
          event: 'save_completed',
          conversationId,
          otpHandoffPending,
          handoffSignal: otpHandoffSignal,
          phone: otpPhone,
          readyForOtp: voiceBookingToolState.readyForOtp,
        });
        setSessionNote('Kaydedildi.', 'success');
      })
      .catch((err: unknown) => {
        console.error('[voice] save failed:', err);
        setSessionNote('Kayıt başarısız oldu.', 'error');
        postVoiceTrace({
          event: 'save_failed',
          conversationId,
          otpHandoffPending,
          handoffSignal: otpHandoffSignal,
          readyForOtp: voiceBookingToolState.readyForOtp,
          note: err instanceof Error ? err.message : String(err),
        });
      })
      .finally(() => {
        pendingSavePromise = null;
      });

    await pendingSavePromise;
    return savedResponse;
  }

  function hideBar() {
    innerBar.style.opacity = '0';
    innerBar.style.pointerEvents = 'none';
    innerBar.style.visibility = 'hidden';
    innerBar.style.transform = 'scale(0.98)';
  }

  function showBar() {
    innerBar.style.opacity = '1';
    innerBar.style.pointerEvents = 'auto';
    innerBar.style.visibility = 'visible';
    innerBar.style.transform = 'scale(1)';
  }

  async function openPanel() {
    container.classList.add('glov-voice-chat-panel-open');
    if (!isMobile()) container.classList.add('glov-voice-chat-expanded');
    hideBar();
    if (isMobile()) {
      panel.classList.add('glov-voice-chat-fullscreen');
      document.body.appendChild(panel);
    }
    syncVisualViewport();
    panel.style.display = 'block';
    await new Promise<void>((r) =>
      requestAnimationFrame(() => {
        panel.classList.add('active');
        r();
      })
    );
  }

  function closePanel() {
    clearVerifiedCloseFallback();
    container.classList.remove('glov-voice-chat-panel-open', 'glov-voice-chat-expanded');
    showBar();
    panel.classList.remove('glov-voice-chat-verifying');
    if (panel.classList.contains('glov-voice-chat-fullscreen')) {
      panel.classList.remove('glov-voice-chat-fullscreen');
      container.appendChild(panel);
    }
    panel.classList.remove('active');
    panel.style.display = 'none';
    stopDurationTimer();
    setPanelState('idle');
  }

  async function getSignedUrl(): Promise<{
    token: string;
    sessionToken?: string;
    userId?: string;
  }> {
    if (!cachedAgentId) {
      const r = await fetch(`${middlewareUrl}/agents/id`);
      if (!r.ok) throw new Error(`Agent ID fetch failed: ${r.status}`);
      const data = (await r.json()) as { agent_id: string; mock?: boolean };
      cachedAgentId = data.agent_id;
      useMockSession = data.mock === true;
    }
    const r = await fetch(
      `${middlewareUrl}/agents/conversations/token?agent_id=${encodeURIComponent(cachedAgentId)}`
    );
    if (!r.ok) throw new Error(`Token fetch failed: ${r.status}`);
    const data = (await r.json()) as { token: string; sessionToken?: string; userId?: string };
    return data;
  }

  async function startSession() {
    const attemptId = ++sessionAttemptId;
    manualShutdown = false;
    clientToolDiagnostics.reset();
    currentConversationId = null;
    voiceTranscript = '';
    userSpeechCount = 0;
    closeUnifiedBookingOtpFlow();
    clearVerifiedCloseFallback();
    verifiedCloseSessionKey = null;
    verifiedCloseSawAssistantSpeech = false;
    stopOtpHold();
    otpVerified = false;
    voiceConfirmationMessage = '';
    smsHandoffAnnounced = false;
    recentUserUtterances = [];
    leadContextUserUtterances = [];
    otpHandoffPending = false;
    otpHandoffSignal = null;
    otpTransferStarted = false;
    setVoiceOtpRetry(null);
    isAssistantSpeaking = false;
    voiceBookingToolState = createEmptyVoiceBookingToolState();
    pendingVoiceBrandModel = null;
    pendingValidationTrace = null;
    clearOtpFallbackTimer();
    clearListeningFallback();
    stopDurationTimer();
    setPanelState('connecting');
    setSessionNote(DEFAULT_SESSION_NOTE, 'neutral');
    traceVoiceState('session-start');
    postVoiceTrace({
      event: 'session_started',
      conversationId: null,
      note: 'client_tools=validate_booking_candidate,start_sms_verification,wait_for_sms_verification',
    });
    await openPanel();
    if (attemptId !== sessionAttemptId) return;
    try {
      const sessionAccess = await getSignedUrl();
      if (attemptId !== sessionAttemptId) return;
      const signedUrl = sessionAccess.token;
      voiceSessionSecret = sessionAccess.sessionToken;
      voiceSessionUserId = sessionAccess.userId;
      let next: VoiceSession | null = null;
      postVoiceTrace({
        event: 'client_tools_registered',
        conversationId: null,
        note: 'validate_booking_candidate,start_sms_verification,wait_for_sms_verification',
      });
      const callbacks: SessionCallbacks = {
        onConnect: (props) => {
          if (attemptId === sessionAttemptId) {
            if (props?.conversationId) {
              currentConversationId = props.conversationId;
            }
            setPanelState('connecting');
            startDurationTimer();
            postVoiceTrace({
              event: 'connected',
              conversationId: currentConversationId,
            });
          }
        },
        onDisconnect: (_details) => {
          if (attemptId !== sessionAttemptId) return;
          clientToolDiagnostics.cancelPending({
            errorType: 'provider_cancelled',
            messageCode: 'provider_disconnected',
          });
          resolveSmsWait(false);
          stopDurationTimer(false);
          const disconnectedConversationId = currentConversationId ?? getConversationId(next);
          currentConversationId = disconnectedConversationId;
          if (manualShutdown) {
            traceVoiceState('disconnect-manual-shutdown', {
              disconnectedConversationId,
            });
            postVoiceTrace({
              event: 'disconnect_manual_shutdown',
              conversationId: disconnectedConversationId,
            });
            manualShutdown = false;
            return;
          }
          postVoiceTrace({
            event: 'disconnected',
            conversationId: disconnectedConversationId,
            otpHandoffPending,
            handoffSignal: otpHandoffSignal,
            readyForOtp: voiceBookingToolState.readyForOtp,
          });
          if (voiceOtpOverlayOpen && !otpVerified) {
            conversation = null;
            setPanelState('waiting_sms');
            setSessionNote(
              'Görüşme sona erdi. SMS doğrulamasına buradan devam edebilirsiniz.',
              'neutral'
            );
            void saveConversationIfNeeded(disconnectedConversationId, 'disconnect');
            return;
          }
          if (getVoiceOtpOpenReason()) {
            void triggerOtpTransfer('disconnect');
          } else {
            conversation = null;
            setPanelState('saving');
            void saveConversationIfNeeded(disconnectedConversationId, 'disconnect').finally(() => {
              closePanel();
            });
          }
        },
        onError: (_message: string, _context?: unknown) => {
          if (attemptId !== sessionAttemptId) return;
          console.error('[voice] ElevenLabs session error');
          stopOtpHold();
          stopDurationTimer(false);
          setSessionNote('Tekrar deneyin.', 'error');
          setPanelState('error');
        },
        onModeChange: (mode: { mode: 'speaking' | 'listening' }) => {
          if (attemptId !== sessionAttemptId) return;
          currentConversationId = getConversationId(next);
          trackVerifiedVoiceCloseMode(mode.mode);
          if (otpVerified) {
            return;
          }
          if (voiceOtpOverlayOpen && !otpVerified) {
            isAssistantSpeaking = mode.mode === 'speaking';
            setPanelState('waiting_sms');
            return;
          }
          if (mode.mode === 'listening') {
            clearListeningFallback();
            isAssistantSpeaking = false;
            if (otpHandoffPending) {
              setPanelState('transferring');
              scheduleOtpFallback(VOICE_OTP_OPEN_AFTER_SPEECH_MS);
              return;
            }
            setPanelState('listening');
            setSessionNote(DEFAULT_SESSION_NOTE, 'neutral');
          } else {
            isAssistantSpeaking = true;
            setPanelState('speaking');
            setSessionNote(DEFAULT_SESSION_NOTE, 'neutral');
            scheduleListeningFallback(attemptId);
          }
        },
        onMessage: (message: { message?: string; role?: string; source?: string }) => {
          if (attemptId !== sessionAttemptId) return;
          if (typeof message.message === 'string' && message.message.trim()) {
            if (voiceOtpOverlayOpen && !otpVerified) {
              traceVoiceState('message-ignored-during-sms', {
                role: message.role ?? null,
                source: message.source ?? null,
              });
              return;
            }
            const spokenText = message.message.trim();
            let tracedAsAssistantAfterValidation = false;
            voiceTranscript += `${message.message}\n`;
            if (message.role === 'user') {
              userSpeechCount += 1;
              clearListeningFallback();
              isAssistantSpeaking = false;
              if (!otpHandoffPending && getCurrentPanelState() !== 'transferring') {
                setPanelState('listening');
                setSessionNote(DEFAULT_SESSION_NOTE, 'neutral');
              }
              recentUserUtterances = [...recentUserUtterances, spokenText].slice(-5);
              leadContextUserUtterances = [...leadContextUserUtterances, spokenText].slice(
                -VOICE_LEAD_CONTEXT_MAX_MESSAGES
              );
              if (
                /\b(?:yedek\s*parca\w*|spare\s+parts?|parca\w*\b.{0,80}\b(?:gelmedi\w*|siparis\w*|bekli\w*)|siparis\w*\b.{0,40}\bparca\w*)\b/u.test(
                  normalizeVoiceBrandText(spokenText).replaceAll('ı', 'i')
                )
              ) {
                // Only corporate purchases and standalone orders are referred; an
                // individual's part request is a service lead. Pause SMS until the
                // validator decides instead of presetting a referral subject.
                voiceBookingToolState = {
                  ...voiceBookingToolState,
                  readyForOtp: false,
                  referralGuidance:
                    'The customer mentioned spare parts. Do not collect personal details or start SMS yet. Call validate_booking_candidate now and follow its answer: it decides between a service request and the spare-parts referral.',
                };
                otpHandoffPending = false;
                otpHandoffSignal = null;
                clearOtpFallbackTimer();
                conversation?.sendContextualUpdate?.(voiceBookingToolState.referralGuidance!);
              }
              const heardBrandModel = extractVoiceBrandModelMention(spokenText);
              if (heardBrandModel) {
                pendingVoiceBrandModel = heardBrandModel;
              }
            } else if (pendingValidationTrace) {
              isAssistantSpeaking = true;
              if (!otpHandoffPending && getCurrentPanelState() !== 'transferring') {
                setPanelState('speaking');
                setSessionNote(DEFAULT_SESSION_NOTE, 'neutral');
                scheduleListeningFallback(attemptId);
              }
              const spokenField = inferVoiceBookingQuestionField(message.message);
              postVoiceTrace({
                event: 'assistant_after_validation',
                conversationId: currentConversationId,
                otpHandoffPending,
                handoffSignal: otpHandoffSignal,
                role: message.role ?? null,
                source: message.source ?? null,
                snippet: summarizeTraceMessage(message.message),
                traceRevision: pendingValidationTrace.traceRevision,
                spokenField,
                bookingIntent: voiceBookingToolState.intent,
                requiredFields: voiceBookingToolState.requiredFields,
                missingFields: voiceBookingToolState.missingFields,
                nextField: voiceBookingToolState.nextField,
                readyForOtp: voiceBookingToolState.readyForOtp,
                validatedFields: voiceBookingToolState.fields,
              });
              tracedAsAssistantAfterValidation = true;
              pendingValidationTrace = null;
            } else if (message.role && message.role !== 'user') {
              isAssistantSpeaking = true;
              if (!otpHandoffPending && getCurrentPanelState() !== 'transferring') {
                setPanelState('speaking');
                setSessionNote(DEFAULT_SESSION_NOTE, 'neutral');
                scheduleListeningFallback(attemptId);
              }
            }
            if (!tracedAsAssistantAfterValidation) {
              postVoiceConversationMessageTrace(message);
            }
            traceVoiceState('message', {
              role: message.role ?? null,
              source: message.source ?? null,
              text: message.message,
            });
            if (
              message.role !== 'user' &&
              voiceBookingToolState.readyForOtp &&
              shouldTriggerOtpTransfer(message.message)
            ) {
              postVoiceTrace({
                event: 'handoff_phrase_detected',
                conversationId: currentConversationId,
                otpHandoffPending,
                handoffSignal: 'phrase',
                role: message.role ?? null,
                source: message.source ?? null,
                snippet: summarizeTraceMessage(message.message),
              });
              markOtpHandoffPending('phrase');
            }
          }
        },
      };

      if (useMockSession || signedUrl.startsWith('mock-token:')) {
        next = startMockSession(callbacks);
      } else {
        next = await startElevenLabsVoiceSession(
          {
            signedUrl,
            ...(voiceSessionUserId ? { userId: voiceSessionUserId } : {}),
            clientTools: {
              validate_booking_candidate: (args?: Record<string, unknown>) =>
                clientToolDiagnostics.run(
                  'validate_booking_candidate',
                  (signal) => validateVoiceBookingCandidateLive(args, signal),
                  () =>
                    JSON.stringify({
                      ok: false,
                      assistant_guidance:
                        'Validation is temporarily unavailable. Do not mention an error to the caller. Apologize briefly and ask only for the same missing detail again.',
                      next_question_suggestion:
                        'Kusura bakmayın, son tercihinizi tam alamadım. Tekrar paylaşır mısınız?',
                    })
                ),
              start_sms_verification: () =>
                clientToolDiagnostics.run('start_sms_verification', () =>
                  startSmsVerificationTool('start_sms_verification')
                ),
              wait_for_sms_verification: () =>
                clientToolDiagnostics.run('wait_for_sms_verification', () =>
                  waitForSmsVerificationTool()
                ),
            },
            ...callbacks,
          },
          elevenLabsSdkUrl
        );
      }
      if (attemptId !== sessionAttemptId) {
        void next.endSession().catch(() => {});
        return;
      }
      conversation = next;
      currentConversationId = getConversationId(next);
      setSessionNote(DEFAULT_SESSION_NOTE, 'neutral');
      traceVoiceState('session-active', {
        useMockSession,
      });
    } catch (err) {
      if (attemptId !== sessionAttemptId) return;
      console.error('[voice] failed:', err);
      stopOtpHold();
      stopDurationTimer(false);
      setSessionNote('Başlatılamadı.', 'error');
      setPanelState('error');
    }
  }

  async function endSession(reason: VoiceEndReason = 'automatic_end') {
    stopOtpHold(otpVerified);
    if (voiceOtpOverlayOpen) {
      closeUnifiedBookingOtpFlow();
    }
    const active = conversation;
    const activeConversationId = currentConversationId ?? getConversationId(active);
    let manualOtpHandoff: { sessionKey: string; allowed: boolean; phone: string | null } | null =
      null;
    clientToolDiagnostics.cancelPending({
      errorType: 'client_cancelled',
      messageCode: 'client_ended',
    });
    resolveSmsWait(false);
    sessionAttemptId++;
    manualShutdown = true;
    conversation = null;
    currentConversationId = activeConversationId;
    stopDurationTimer(false);

    if (!active) {
      closePanel();
      return;
    }

    setPanelState('saving');
    endBtn.disabled = true;
    microphoneBtn.disabled = true;
    speakerBtn.disabled = true;

    try {
      await active.endSession().catch((err: unknown) => console.error('[voice]', err));
      traceVoiceState('manual-end', { activeConversationId });
      const saveResponse = await saveConversationIfNeeded(activeConversationId, reason);
      if (!otpVerified && activeConversationId) {
        const sessionKey = getVoiceOtpSessionKey(activeConversationId);
        const otpHandoff = resolveVoiceOtpHandoff('manual_end', sessionKey, saveResponse);
        manualOtpHandoff = { sessionKey, ...otpHandoff };
      }
    } finally {
      manualShutdown = false;
      endBtn.disabled = false;
      microphoneBtn.disabled = false;
      speakerBtn.disabled = false;
      otpHandoffPending = false;
      otpHandoffSignal = null;
      otpTransferStarted = false;
      clearOtpFallbackTimer();
      isAssistantSpeaking = false;
      closePanel();
      if (manualOtpHandoff) {
        openResolvedVoiceOtpHandoff(manualOtpHandoff.sessionKey, manualOtpHandoff);
      }
    }
  }

  function syncVisualViewport() {
    const viewport = window.visualViewport;
    // Pinch-zoom shrinks the visual viewport; keep the full panel so panning reaches the controls.
    if (viewport && Math.abs(viewport.scale - 1) > 0.01) {
      panel.style.removeProperty('--glov-voice-viewport-height');
      panel.style.removeProperty('--glov-voice-viewport-top');
      return;
    }
    panel.style.setProperty(
      '--glov-voice-viewport-height',
      `${viewport?.height ?? window.innerHeight}px`
    );
    panel.style.setProperty('--glov-voice-viewport-top', `${viewport?.offsetTop ?? 0}px`);
  }

  function syncLayout() {
    syncVisualViewport();
    const mobile = isMobile();
    container.classList.toggle('glov-voice-chat-mobile', mobile);
    container.classList.remove('glov-voice-chat-desktop-hidden');
    callLabel.textContent = 'Uzmanla Görüş';
    enforceVoiceCallIcon();

    if (!mobile) {
      if (panel.classList.contains('active')) {
        closePanel();
      }
      if (conversation) {
        void endSession();
      }
    }
  }

  let lastPress = 0;

  function suppressNextDocumentTap(durationMs = 480) {
    const blocker = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    const clickOpts: AddEventListenerOptions = { capture: true };
    const touchOpts: AddEventListenerOptions = { capture: true, passive: false };
    document.addEventListener('click', blocker, clickOpts);
    document.addEventListener('touchend', blocker, touchOpts);
    window.setTimeout(() => {
      document.removeEventListener('click', blocker, clickOpts);
      document.removeEventListener('touchend', blocker, touchOpts);
    }, durationMs);
  }

  function bindPress(
    el: HTMLElement,
    handler: () => Promise<void> | void,
    opts?: { suppressGhostTap?: boolean }
  ) {
    const run = (e: Event) => {
      const now = Date.now();
      if (now - lastPress < 250) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      lastPress = now;
      e.preventDefault();
      e.stopPropagation();
      if (opts?.suppressGhostTap) {
        suppressNextDocumentTap();
      }
      void handler();
    };
    el.addEventListener('pointerup', run);
    el.addEventListener('click', run);
    if (opts?.suppressGhostTap) {
      el.addEventListener(
        'touchend',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        { passive: false }
      );
    }
  }

  bindPress(callButton, () => {
    onOpenChat?.();
  });
  for (const intentButton of intentButtons) {
    bindPress(intentButton, () => {
      if (isMobile()) return;
      onOpenChat?.(intentButton.dataset.message);
    });
  }
  bindPress(openChatBtn, () => {
    if (!isMobile()) return;
    return startSession();
  });
  bindPress(otpRetryButton, () => {
    if (!voiceOtpRetry) return;
    void openPanel().then(() => {
      openVoiceOtpFlowOnce(voiceOtpRetry!.sessionKey, voiceOtpRetry!.phone);
    });
  });
  bindPress(microphoneBtn, () => {
    microphoneBtn.classList.toggle('glov-voice-chat-control-button-muted');
    const muted = microphoneBtn.classList.contains('glov-voice-chat-control-button-muted');
    microphoneBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
  });
  bindPress(speakerBtn, () => {
    speakerBtn.classList.toggle('glov-voice-chat-control-button-muted');
    const muted = speakerBtn.classList.contains('glov-voice-chat-control-button-muted');
    speakerBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
  });
  bindPress(endBtn, () => endSession('end_button'), { suppressGhostTap: true });

  setPanelState('idle');
  enforceVoiceCallIcon();
  const openChatIconObserver = new MutationObserver(() => {
    enforceVoiceCallIcon();
  });
  openChatIconObserver.observe(openChatBtn, {
    childList: true,
    subtree: true,
  });
  syncLayout();
  const emitVoiceLifecycle = (
    reason: OtpFunnelClientReason,
    outcome: 'abandoned' | 'reset' = 'abandoned'
  ) => {
    const id = currentConversationId ?? getConversationId(conversation);
    if (!conversation || !id) return;
    emitOtpFunnelClientEvent({
      event: 'voice_lifecycle',
      outcome,
      reason,
      source: 'voice',
      threadId: id,
    });
  };
  const onVoiceVisibility = () => {
    const reason =
      document.visibilityState === 'hidden'
        ? 'page_hidden'
        : navigator.onLine === false
          ? 'offline'
          : 'none';
    emitVoiceLifecycle(reason, reason === 'none' ? 'reset' : 'abandoned');
  };
  const onVoicePageHide = () => emitVoiceLifecycle('page_unload');
  const onVoiceOffline = () => emitVoiceLifecycle('offline');
  const onVoiceOnline = onVoiceVisibility;
  document.addEventListener('visibilitychange', onVoiceVisibility);
  window.addEventListener('pagehide', onVoicePageHide);
  window.addEventListener('offline', onVoiceOffline);
  window.addEventListener('online', onVoiceOnline);
  window.addEventListener('resize', syncLayout);
  window.visualViewport?.addEventListener('resize', syncVisualViewport);
  window.visualViewport?.addEventListener('scroll', syncVisualViewport);

  // Return cleanup
  return () => {
    emitVoiceLifecycle('widget_destroyed');
    document.removeEventListener('visibilitychange', onVoiceVisibility);
    window.removeEventListener('pagehide', onVoicePageHide);
    window.removeEventListener('offline', onVoiceOffline);
    window.removeEventListener('online', onVoiceOnline);
    window.removeEventListener('resize', syncLayout);
    window.visualViewport?.removeEventListener('resize', syncVisualViewport);
    window.visualViewport?.removeEventListener('scroll', syncVisualViewport);
    openChatIconObserver.disconnect();
    clearOtpFallbackTimer();
    stopOtpHold();
    closeUnifiedBookingOtpFlow();
    if (conversation) {
      void conversation.endSession().catch(() => {});
      conversation = null;
    }
    container.remove();
    if (panel.parentElement !== container) panel.remove();
  };
}
